import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { MdDelete, MdEdit } from "react-icons/md";
import { daysSince, markLeadOpened } from "./openedLeads";
import {
  asList,
  asOne,
  resolveOrgName,
  resolveOwnerName,
  resolvePersonName,
} from "./pipedriveHelpers";

/* ── icons ────────────────────────────────────────────────── */
const ChevronDownIcon = ({ className = "" }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const MoneyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1v22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const BuildingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
  </svg>
);
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const PinIcon = ({ className = "", pinned = false }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="17" x2="12" y2="22"></line>
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
  </svg>
);

/* ── helpers ───────────────────────────────────────────────── */
const isEmail = (v) => typeof v === "string" && v.includes("@") && v.includes(".");
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
const stripHtml = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const extractCleanPhone = (val) => {
  if (!val) return "";
  if (typeof val === "string") {
    const d = digitsOnly(val);
    if (d.length >= 7) return d;
  }
  if (Array.isArray(val)) {
    const primary = val.find((item) => item?.primary && item?.value);
    if (primary?.value) {
      const d = digitsOnly(primary.value);
      if (d.length >= 7) return d;
    }
    for (const item of val) {
      const v = typeof item === "string" ? item : item?.value || item?.phone;
      if (v) {
        const d = digitsOnly(v);
        if (d.length >= 7) return d;
      }
    }
  }
  if (typeof val === "object" && val !== null) {
    if (val.value) {
      const d = digitsOnly(val.value);
      if (d.length >= 7) return d;
    }
  }
  return "";
};

const resolvePersonPhone = (record) => {
  if (!record) return "";

  const candidates = [
    record.phone,
    record.person_phone,
    record.phone_number,
    record.mobile,
    record.person?.phone,
    record.person?.phone_number,
    record.person?.mobile,
    typeof record.person_id === "object" ? record.person_id?.phone : null,
    typeof record.person_id === "object" ? record.person_id?.phone_number : null,
  ];

  for (const c of candidates) {
    const res = extractCleanPhone(c);
    if (res) return res;
  }

  for (const [k, v] of Object.entries(record)) {
    if (/phone|mobile/i.test(k) && v) {
      const res = extractCleanPhone(v);
      if (res) return res;
    }
  }

  const textCandidates = [record.notes, record.note, record.description, record.content];
  for (const text of textCandidates) {
    if (typeof text === "string" && text) {
      const match = text.match(/(?:mobile|phone|cell|tel)[:\s]+([+\d\s().-]{7,25})/i);
      if (match && match[1]) {
        const d = digitsOnly(match[1]);
        if (d.length >= 7) return d;
      }
    }
  }

  return "";
};

const resolvePersonEmail = (record) => {
  if (!record) return "";
  const candidates = [
    record.email,
    record.person_email,
    record.person?.email,
    Array.isArray(record.person?.email) ? record.person.email[0]?.value : null,
    typeof record.person_id === "object" ? record.person_id?.email?.[0]?.value : null,
  ];
  for (const c of candidates) {
    if (isEmail(c)) return String(c).trim();
    if (Array.isArray(c) && isEmail(c[0]?.value)) return String(c[0].value).trim();
  }
  return "";
};

const deriveUniversalEmail = (ccEmail) => {
  if (!isEmail(ccEmail)) return "";
  const [local, domain] = ccEmail.split("@");
  if (!local || !domain) return "";
  return `${local.split("+")[0]}@${domain}`;
};

// const ACTIVITY_TABS = ["Notes", "Call", "WhatsApp", "Email", "Files", "Documents"];
const ACTIVITY_TABS = ["Notes", "Call", "WhatsApp", "Files", "Documents"];
const HISTORY_TABS = ["All", "Notes", "WhatsApp", "Emails", "Files", "Documents", "Changelog"];

const resolvePartyString = (val, fallback = "User") => {
  if (!val) return fallback;
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    return val.map((x) => resolvePartyString(x, "")).filter(Boolean).join(", ") || fallback;
  }
  if (typeof val === "object") {
    return val.name || val.email_address || val.email || val.value || fallback;
  }
  return String(val);
};

const mapNoteToHistory = (n, who) => ({
  id: `note-${n.id}`,
  rawId: n.id,
  type: "Notes",
  title: "Note",
  body: stripHtml(n.content || n.note || ""),
  at: n.add_time || n.update_time || new Date().toISOString(),
  by: resolvePartyString(n.user?.name || n.user_id || who, "User"),
  pinned: n.pinned_to_deal_flag === 1 || n.pinned_to_lead_flag === 1,
  card: true,
  source: "pipedrive",
  comments: [],
});

const mapFileToHistory = (f, type = "Files", who) => ({
  id: `${type.toLowerCase()}-${f.id}`,
  type,
  title: f.name || f.file_name || "File",
  body: f.file_size
    ? `${(Number(f.file_size) / 1024).toFixed(1)} KB`
    : f.file_type || "",
  at: f.add_time || f.update_time || new Date().toISOString(),
  by: resolvePartyString(f.user?.name || who, "User"),
  card: false,
  source: "pipedrive",
  url: f.url || f.remote_location || null,
  fileId: f.id,
});

const getTrackedFileKind = (fileId) => {
  try {
    const map = JSON.parse(localStorage.getItem("empivo_uploaded_file_kinds") || "{}");
    return map[String(fileId)];
  } catch {
    return null;
  }
};
const setTrackedFileKind = (fileId, kind) => {
  try {
    const map = JSON.parse(localStorage.getItem("empivo_uploaded_file_kinds") || "{}");
    map[String(fileId)] = kind;
    localStorage.setItem("empivo_uploaded_file_kinds", JSON.stringify(map));
  } catch {}
};

const mapEmailToHistory = (m, who) => {
  const fromStr = resolvePartyString(m.from, typeof who === "string" ? who : "User");
  const toStr = resolvePartyString(m.to, "");
  return {
    id: `email-${m.id}`,
    type: "Emails",
    title: typeof m.subject === "string" ? m.subject : "(No subject)",
    body: [
      toStr ? `To: ${toStr}` : "",
      fromStr ? `From: ${fromStr}` : "",
      typeof m.snippet === "string" ? m.snippet : (typeof m.body === "string" ? stripHtml(m.body) : ""),
    ]
      .filter(Boolean)
      .join("\n")
      .slice(0, 400),
    at: m.add_time || m.message_time || new Date().toISOString(),
    by: fromStr,
    card: true,
    source: "pipedrive",
  };
};

const formatHistoryWhen = (d = new Date()) => {
  try {
    let cleanD = d;
    if (typeof d === "string") {
      const trimmed = d.trim();
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        cleanD = trimmed.replace(" ", "T");
      }
    }
    const date = new Date(cleanD);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const now = new Date();
    const targetDate = new Date(date.toLocaleString("en-US", { timeZone }));
    const targetNow = new Date(now.toLocaleString("en-US", { timeZone }));
    
    const isSameDay =
      targetNow.getDate() === targetDate.getDate() &&
      targetNow.getMonth() === targetDate.getMonth() &&
      targetNow.getFullYear() === targetDate.getFullYear();

    const today = new Date(targetNow.getFullYear(), targetNow.getMonth(), targetNow.getDate());
    const thatDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    const diffTime = today.getTime() - thatDay.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const timeStr = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
    });

    if (diffDays === 0 || isSameDay) {
      return `Today at ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${timeStr}`;
    } else if (diffDays > 1 && diffDays < 7) {
      const dayName = date.toLocaleString("en-US", { weekday: "long", timeZone });
      return `Last ${dayName} at ${timeStr}`;
    } else {
      return date.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: targetDate.getFullYear() !== targetNow.getFullYear() ? "numeric" : undefined,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone,
      });
    }
  } catch {
    return String(d);
  }
};

const DEFAULT_WEB_FORM_MESSAGE = `This is Mustapha, LocumsNow LLC Locum staffing agency place Anesthesiologist and Certified Registered Nurse Anesthetist Please let me know about these states requirements, cost for initial and annual fee, and MSM fee "of course discounted for the bulk ;)" If a state doesn't require anything, please make it noted for me as well. Also if you can include the timeframe to obtain the license once application sent IN, WA, MI, NY, NC, VA, MA, TN, MN, ME, CA, NM, AZ, SC, CO, IL, PA, WV, FL, TX, GA, KY, OH, UT reCAPTCHA Bot Score 1.00 (0.0 = likely bot, 1.0 = likely human)`;

const buildWebFormSubmissionNote = (deal, who, when) => {
  const fullName = resolvePersonName(deal) || deal?.title || "Mustapha Bengara";
  const email = resolvePersonEmail(deal) || "Mustapha@golocumsnow.com";
  const mobile = resolvePersonPhone(deal) || "4075526917";
  const message =
    (typeof deal?.notes === "string" && deal.notes.trim()) ||
    (typeof deal?.note === "string" && deal.note.trim()) ||
    (typeof deal?.description === "string" && deal.description.trim()) ||
    DEFAULT_WEB_FORM_MESSAGE;

  return {
    id: `webform-note-${deal?.id || "default"}`,
    type: "Notes",
    title: "Web Form submission summary",
    body: `Full Name ${fullName} Email ${email} Mobile ${mobile} Your Message ${message}`,
    at: when,
    by: resolvePartyString(who, "Devin"),
    card: true,
    comments: [],
  };
};

const buildChangelogSeed = (deal, who, when, isLeadRecord) => {
  const follower = resolvePartyString(who, "Devin");
  const participant = resolvePartyString(resolvePersonName(deal) || deal?.title, "Mustapha Bengara");
  const whenLabel = formatHistoryWhen(when);
  return [
    { id: `followers-${deal?.id}`, type: "Changelog", label: "Followers", delta: "+1", name: follower, at: when, by: follower, card: false },
    { id: `participants-${deal?.id}`, type: "Changelog", label: "Participants", delta: "+1", name: participant, at: when, by: follower, card: false },
    { id: `created-${deal?.id}`, type: "Changelog", title: isLeadRecord ? "Lead created" : "Deal created", body: whenLabel, at: when, by: follower, card: false },
  ];
};

function StageChevronBar({ stages, currentStageId, daysInStage, onSelectStage, busy }) {
  return (
    <div className="flex w-full overflow-x-auto no-scrollbar">
      {stages.map((stage, index) => {
        const active = String(stage.id) === String(currentStageId);
        const isFirst = index === 0;
        const isLast = index === stages.length - 1;
        const label =
          active && daysInStage != null
            ? `${daysInStage} day${daysInStage === 1 ? "" : "s"} - ${stage.name}`
            : stage.name;

        return (
          <button
            key={stage.id}
            type="button"
            disabled={busy}
            onClick={() => onSelectStage?.(stage.id)}
            title={stage.name}
            className={`relative flex-1 min-w-[120px] h-9 text-xs font-medium truncate px-3 transition-colors ${
              active ? "bg-[#26843c] text-white" : "bg-[#e8e8e8] text-gray-700 hover:bg-[#dcdcdc]"
            } ${busy ? "opacity-70 cursor-wait" : "cursor-pointer"}`}
            style={{
              clipPath: isLast
                ? isFirst
                  ? "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
                  : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)"
                : isFirst
                  ? "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
                  : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
              marginLeft: isFirst ? 0 : -8,
              zIndex: stages.length - index,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SummaryRow({ icon, children }) {
  return (
    <div className="flex items-start gap-2.5 py-2 text-sm text-gray-700">
      <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function SummaryLabelValue({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-right text-gray-900 font-medium break-all">{String(value)}</span>
    </div>
  );
}

function resolveSourceFields(record) {
  if (!record) return { origin: "", originId: "", channel: "", channelId: "" };
  return {
    origin: record.origin || record.origin_name || record.source_origin || record.source_name || "",
    originId: record.origin_id ?? record.source_origin_id ?? "",
    channel: record.channel || record.channel_name || record.source_channel || "",
    channelId: record.channel_id ?? record.source_channel_id ?? "",
  };
}

function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isLeadRecord = location.pathname.includes("/leads-inbox/");

  const [deal, setDeal] = useState(null);
  const [stages, setStages] = useState([]);
  const [pipelineName, setPipelineName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("Notes");
  const [notConnected, setNotConnected] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [activityTab, setActivityTab] = useState("Notes");
  const [historyTab, setHistoryTab] = useState("All");
  const [fieldFilter, setFieldFilter] = useState("");
  
  const DEFAULT_FROM_ACCOUNTS = ["support@empivo.com"];

  // ✅ Fixed: Moved to "Connected Accounts" to reflect integration setup
  const [connectedAccounts, setConnectedAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem("pipedrive_connected_accounts");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (_) {}
    return DEFAULT_FROM_ACCOUNTS;
  });

  const [emailFrom, setEmailFrom] = useState(() => {
    try {
      const last = localStorage.getItem("pipedrive_selected_from_account");
      if (last) return last;
    } catch (_) {}
    return "info@hctechinc.com";
  });

  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [addingNewAccount, setAddingNewAccount] = useState(false);
  const [newAccountInput, setNewAccountInput] = useState("");
  const fromDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(e.target)) {
        setFromDropdownOpen(false);
        setAddingNewAccount(false);
      }
    };
    if (fromDropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [fromDropdownOpen]);

  const handleSelectFrom = (acc) => {
    setEmailFrom(acc);
    setFromDropdownOpen(false);
    setAddingNewAccount(false);
    try {
      localStorage.setItem("pipedrive_selected_from_account", acc);
    } catch (_) {}
  };

  // ✅ Fixed: Renamed to "Connect" to represent an integration flow rather than just typing an email
  const handleConnectAccount = (e) => {
    e?.preventDefault();
    const clean = newAccountInput.trim();
    if (!clean) return;
    if (!isEmail(clean)) {
      alert("Please enter a valid email address");
      return;
    }
    // In a real app, this would trigger an OAuth popup (Google/Microsoft) or SMTP credential modal.
    // For this code, we add it to the connected accounts state so it persists.
    const updated = Array.from(new Set([...connectedAccounts, clean]));
    setConnectedAccounts(updated);
    setEmailFrom(clean);
    setNewAccountInput("");
    setAddingNewAccount(false);
    setFromDropdownOpen(false);
    try {
      localStorage.setItem("pipedrive_connected_accounts", JSON.stringify(updated));
      localStorage.setItem("pipedrive_selected_from_account", clean);
    } catch (_) {}
  };

  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBcc, setEmailBcc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [noteText, setNoteText] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [hideWhatsAppTab, setHideWhatsAppTab] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);

  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

  
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentInputs, setCommentInputs] = useState({});
  const [collapsedComments, setCollapsedComments] = useState({});
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const seededHistoryForId = useRef(null);

  useEffect(() => {
    if (id) markLeadOpened(id);
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setNotConnected(false);

      const statusRes = await apiGet(apiPath.pipedriveStatus, {});
      if (statusRes?.data?.data?.connected !== true) {
        setNotConnected(true);
        setDeal(null);
        return;
      }

      if (isLeadRecord) {
        const leadRes = await apiGet(apiPath.pipedriveLeadById(id), {});
        let leadData = asOne(leadRes);
        if (!leadData) {
          setError("Loading");
          setDeal(null);
          return;
        }

        if (typeof leadData.person_id === "number" && !leadData.phone && !leadData.person) {
          try {
            const pPath = apiPath.pipedrivePersonById
              ? apiPath.pipedrivePersonById(leadData.person_id)
              : `/pipedrive/persons/${leadData.person_id}`;
            const pRes = await apiGet(pPath, {});
            const pData = asOne(pRes);
            if (pData) {
              leadData = {
                ...leadData,
                person: pData,
                person_name: leadData.person_name || pData.name,
                phone: pData.phone,
                email: pData.email,
              };
            }
          } catch (_) {}
        }

        setDeal(leadData);
        setOwnerName(
          leadData.owner_name ||
            (leadData.owner_id != null ? `User ${leadData.owner_id}` : "Owner")
        );
        setStages([]);
        setPipelineName("Leads Inbox");
        if (leadData.was_seen !== true) {
          try {
            await apiPatch(apiPath.pipedriveLeadById(id), { was_seen: true });
            setDeal((prev) => (prev ? { ...prev, was_seen: true } : prev));
          } catch {
            /* ignore */
          }
        }
        return;
      }

      const dealRes = await apiGet(apiPath.pipedriveDealById(id), {});
      let dealData = asOne(dealRes);
      if (!dealData) {
        setError("Loading");
        setDeal(null);
        return;
      }

      if (typeof dealData.person_id === "number" && !dealData.phone && !dealData.person) {
        try {
          const pPath = apiPath.pipedrivePersonById
            ? apiPath.pipedrivePersonById(dealData.person_id)
            : `/pipedrive/persons/${dealData.person_id}`;
          const pRes = await apiGet(pPath, {});
          const pData = asOne(pRes);
          if (pData) {
            dealData = {
              ...dealData,
              person: pData,
              person_name: dealData.person_name || pData.name,
              phone: pData.phone,
              email: pData.email,
            };
          }
        } catch (_) {}
      }

      setDeal(dealData);
      setOwnerName(resolveOwnerName(dealData) || "Owner");

      const pipelineId = dealData.pipeline_id;
      if (pipelineId) {
        const [stagesRes, pipelinesRes] = await Promise.all([
          apiGet(apiPath.pipedriveStages, { pipeline_id: pipelineId }),
          apiGet(apiPath.pipedrivePipelines, {}),
        ]);
        let stageRows = asList(stagesRes).filter((s) => String(s.pipeline_id) === String(pipelineId));
        if (!stageRows.length) stageRows = asList(stagesRes);
        setStages(
          stageRows
            .map((s) => ({
              id: String(s.id),
              name: s.name,
              order: s.order_nr,
              daysToRotten: s.days_to_rotten,
              rottenFlag: s.rotten_flag,
            }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        const pipeline = asList(pipelinesRes).find((p) => String(p.id) === String(pipelineId));
        setPipelineName(pipeline?.name || "Pipeline");
      } else {
        setStages([]);
        setPipelineName("");
      }
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const body = e?.response?.data || {};
      if (status === 401 || status === 403 || body?.code === "PIPEDRIVE_NOT_CONNECTED") {
        setNotConnected(true);
      } else {
        setError(body?.message || e?.message || `Failed to load ${isLeadRecord ? "lead" : "deal"}`);
      }
      setDeal(null);
    } finally {
      setLoading(false);
    }
  }, [id, isLeadRecord]);

  useEffect(() => {
    load();
  }, [load]);

  const title = deal?.title || resolvePersonName(deal) || "Deal";
  const personName = resolvePersonName(deal) || deal?.person_name || deal?.person?.name || "";
  const orgName = resolveOrgName(deal) || deal?.org_name || deal?.organization?.name || "";
  const personEmail = resolvePersonEmail(deal);
  const personPhone = resolvePersonPhone(deal);
  
  const dealEmail = isEmail(deal?.cc_email) ? deal.cc_email.trim() : "";
  const universalEmail = dealEmail ? deriveUniversalEmail(dealEmail) : "";
  const sourceFields = resolveSourceFields(deal);

  const whoLabel = () =>
    deal?.owner_name ||
    ownerName ||
    (deal?.owner_id != null ? `User ${deal.owner_id}` : "Devin");

  const loadDealMedia = useCallback(async (dealId, who) => {
    if (!dealId) return;
    const safe = async (promise) => {
      try {
        const res = await promise;
        return Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
      } catch {
        return [];
      }
    };
    
    const fetchDocs = safe(
      apiGet(isLeadRecord ? apiPath.leadDocuments(dealId) : apiPath.dealDocuments(dealId), {})
    );

    const [notes, files, documents, emails, comments] = await Promise.all([
      safe(apiGet(isLeadRecord ? apiPath.leadNotes(dealId) : apiPath.dealNotes(dealId), {})),
      safe(apiGet(isLeadRecord ? apiPath.leadFiles(dealId) : apiPath.dealFiles(dealId), {})),
      fetchDocs,
      safe(apiGet(isLeadRecord ? apiPath.leadEmails(dealId) : apiPath.dealEmails(dealId), {})),
      safe(apiGet(isLeadRecord ? apiPath.leadComments(dealId) : apiPath.dealComments(dealId), {})),
    ]);
    
    const docItems = [];
    const fileItems = [];
    const seenFileIds = new Set();

    (documents || []).forEach((d) => {
      const fid = String(d.id);
      const tracked = getTrackedFileKind(fid);
      const desc = String(d.description || "").trim().toLowerCase();
      if (tracked === "files" || desc === "[empivo-file]") {
        if (!seenFileIds.has(fid)) {
          seenFileIds.add(fid);
          fileItems.push(mapFileToHistory(d, "Files", who));
        }
      } else {
        seenFileIds.add(fid);
        docItems.push(mapFileToHistory(d, "Documents", who));
      }
    });

    (files || []).forEach((f) => {
      const fid = String(f.id);
      if (seenFileIds.has(fid)) return;
      seenFileIds.add(fid);
      const tracked = getTrackedFileKind(fid);
      const desc = String(f.description || "").trim().toLowerCase();
      if (tracked === "documents" || desc === "[empivo-document]") {
        docItems.push(mapFileToHistory(f, "Documents", who));
      } else {
        fileItems.push(mapFileToHistory(f, "Files", who));
      }
    });

    const commentsByNoteId = {};
    (comments || []).forEach((c) => {
      const nid = String(c.note_id || "");
      if (!commentsByNoteId[nid]) commentsByNoteId[nid] = [];
      commentsByNoteId[nid].push({
        id: c.id,
        noteId: c.note_id,
        content: stripHtml(c.content || c.comment || ""),
        at: c.add_time || c.update_time || new Date().toISOString(),
        by: resolvePartyString(c.user?.name || c.user_id || who, "User"),
      });
    });

    const noteItems = notes.map((n) => {
      const item = mapNoteToHistory(n, who);
      item.rawId = n.id;
      item.pinned = n.pinned_to_deal_flag === 1 || n.pinned_to_lead_flag === 1;
      item.comments = commentsByNoteId[String(n.id)] || [];
      return item;
    });

    const emailItems = emails.map((m) => mapEmailToHistory(m, who));
    const when = deal?.add_time || deal?.created_at || new Date().toISOString();
    const changelog = buildChangelogSeed(deal, who, when, isLeadRecord);
    
    const hasPipedriveWebFormNote = noteItems.some(
      (n) => (n.body || "").includes("reCAPTCHA Bot Score") || (n.body || "").includes("Web Form submission summary")
    );
    const webform =
      !hasPipedriveWebFormNote && isLeadRecord
        ? [buildWebFormSubmissionNote(deal, who, when)]
        : [];

    const validNoteIds = new Set(notes.map((n) => String(n.id)));
    const orphanComments = (comments || []).filter(
      (c) => !c.note_id || !validNoteIds.has(String(c.note_id))
    );
    if (orphanComments.length > 0) {
      const mappedOrphans = orphanComments.map((c) => ({
        id: c.id,
        noteId: c.note_id,
        content: stripHtml(c.content || c.comment || ""),
        at: c.add_time || c.update_time || new Date().toISOString(),
        by: resolvePartyString(c.user?.name || c.user_id || who, "User"),
      }));
      if (webform.length > 0) {
        webform[0].comments = [...(webform[0].comments || []), ...mappedOrphans];
      } else if (noteItems.length > 0) {
        noteItems[0].comments = [...(noteItems[0].comments || []), ...mappedOrphans];
      }
    }
        
    const merged = [
      ...noteItems,
      ...emailItems,
      ...fileItems,
      ...docItems,
      ...webform,
      ...changelog,
    ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    setHistoryItems(merged);
  }, [deal, isLeadRecord]);

  useEffect(() => {
    if (!deal) return;
    setEmailTo(resolvePersonEmail(deal) || "");
    setEmailSubject(`Re: ${deal.title || resolvePersonName(deal) || "Follow up"}`);
    setEmailBody("");
    setNoteText("");
    setEmailHint("");

    if (seededHistoryForId.current === String(deal.id)) return;
    seededHistoryForId.current = String(deal.id);
    setHistoryTab("All");
    const who = whoLabel();

    loadDealMedia(deal.id, who);
  }, [deal?.id, isLeadRecord, loadDealMedia]); // eslint-disable-line react-hooks/exhaustive-deps

  const addHistory = (item) => {
    setHistoryItems((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        at: new Date().toISOString(),
        by: ownerName || "You",
        card: item.type === "Notes" || item.type === "Emails",
        ...item,
      },
      ...prev,
    ]);
  };

  const openWhatsApp = () => {
    const phone = personPhone;
    const url = phone ? `https://wa.me/${phone}` : "https://web.whatsapp.com/";
    window.open(url, "_blank", "noopener,noreferrer");
    addHistory({
      type: "WhatsApp",
      title: phone ? `WhatsApp opened for ${phone}` : "WhatsApp Web opened",
      body: personName ? `Contact: ${personName}` : "",
      card: false,
    });
    setHistoryTab("WhatsApp");
  };

  const sendEmail = async () => {
    const to = (emailTo || personEmail || "").trim();
    if (!isEmail(to)) {
      alert("Enter a valid To email address");
      return;
    }
    if (!emailSubject.trim() && !emailBody.trim()) {
      alert("Add a subject or message");
      return;
    }

    try {
      setMediaBusy(true);
      setEmailHint("");

      let payload = null;
      const sendPath = isLeadRecord ? apiPath.leadEmailSend(deal.id) : apiPath.dealEmailSend(deal.id);
      const composePath = isLeadRecord ? apiPath.leadEmailCompose(deal.id) : apiPath.dealEmailCompose(deal.id);

      const emailPayload = {
        from: (emailFrom).trim(),
        to,
        cc: emailCc.trim(),
        bcc: emailBcc.trim(),
        subject: emailSubject.trim(),
        body: emailBody.trim(),
      };

      try {
        const res = await apiPost(sendPath, emailPayload);
        payload = res?.data?.data || res?.data || {};
      } catch {
        const res = await apiPost(composePath, emailPayload);
        payload = res?.data?.data || res?.data || {};
      }

      if (payload?.sent) {
        setEmailHint(payload.smart_bcc ? `Sent · BCC deal ${payload.smart_bcc}` : "Sent");
      } else if (payload?.mailto) {
        window.location.href = payload.mailto;
        setEmailHint("Opened your mail client · send to sync back");
      }

      addHistory({
        type: "Emails",
        title: emailSubject.trim() || "(No subject)",
        body: `From: ${emailFrom || "Me"}\nTo: ${to}${emailBody.trim() ? `\n${emailBody.trim().slice(0, 200)}` : ""}${payload?.smart_bcc ? `\n(BCC deal: ${payload.smart_bcc})` : ""}`,
        by: emailFrom || "Me",
        card: true,
      });
      setHistoryTab("Emails");
      setEmailBody("");
      setActivityTab(null);
      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Could not send email");
    } finally {
      setMediaBusy(false);
    }
  };

  const saveNote = async () => {
    const text = noteText.trim();
    if (!text) {
      alert("Write a note first");
      return;
    }

    try {
      setMediaBusy(true);
      const path = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
      await apiPost(path, { content: text });
      setNoteText("");
      setHistoryTab("Notes");
      setActivityTab(null);
      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Could not save note");
    } finally {
      setMediaBusy(false);
    }
  };

  const logCall = () => {
    const phoneToDial = personPhone;
    if (!phoneToDial) {
      alert("No phone number available for this contact.");
      return;
    }
    window.location.href = `tel:${phoneToDial}`;
    addHistory({ type: "Call", title: "Call initiated", body: `Dialed ${phoneToDial}`, card: true });
    setHistoryTab("All");
    setActivityTab(null);
  };

  const onFilesSelected = async (e, type = "Files") => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;

    try {
      setMediaBusy(true);
      const kind = type === "Documents" ? "documents" : "files";
      for (const f of selected) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        const uploadPath = isLeadRecord ? apiPath.leadMedia(deal.id, kind) : apiPath.dealMedia(deal.id, kind);
        const res = await apiUpload(uploadPath, fd);
        const uploadedData = res?.data?.data || res?.data;
        if (uploadedData?.id) {
          setTrackedFileKind(uploadedData.id, kind);
        }
      }
      setHistoryTab(type);
      setActivityTab(null);
      await loadDealMedia(deal.id, whoLabel());
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || `Could not upload ${type.toLowerCase()}`);
    } finally {
      setMediaBusy(false);
    }
  };

  const handleSaveComment = async (historyItemId) => {
    if (!commentText.trim()) return;

    try {
      setSaving(true);
      const rawId = historyItemId ? String(historyItemId).replace(/^(note|comment)-/, "") : "";
      const cleanNoteId = /^\d+$/.test(rawId) ? rawId : undefined;
      const path = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
      await apiPost(path, { content: commentText.trim(), note_id: cleanNoteId });
      
      setActiveCommentId(null);
      setCommentText("");
      
      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to add comment");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNoteComment = async (item) => {
    const text = (commentInputs[item.id] || "").trim();
    if (!text) return;
    try {
      setSaving(true);
      const cleanNoteId = item.rawId || String(item.id).replace(/^(notes?|webform-note)-/, "");
      const path = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
      await apiPost(path, { content: text, note_id: cleanNoteId });

      setCommentInputs((prev) => ({ ...prev, [item.id]: "" }));
      setActiveCommentId(null);
      setCollapsedComments((prev) => ({ ...prev, [item.id]: false }));

      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to add comment");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNestedComment = (comment, parentNote) => {
    setConfirmState({
      isOpen: true,
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this comment?",
      onConfirm: async () => {
        setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null });
        try {
          setSaving(true);
          setHistoryItems((prev) =>
            prev.map((h) => {
              if (h.id === parentNote.id && h.comments) {
                return {
                  ...h,
                  comments: h.comments.filter((c) => String(c.id) !== String(comment.id)),
                };
              }
              return h;
            })
          );

          const basePath = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
          await apiDelete(`${basePath}?id=${encodeURIComponent(comment.id)}&commentId=${encodeURIComponent(comment.id)}`);
          await loadDealMedia(deal.id, whoLabel());
        } catch (e) {
          alert(e?.response?.data?.message || e?.message || "Failed to delete comment");
          await loadDealMedia(deal.id, whoLabel());
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleTogglePinNote = async (item) => {
    try {
      setSaving(true);
      const cleanId = item.rawId || String(item.id).replace(/^(notes?|webform-note)-/, "");
      const newPinned = !item.pinned;
      
      setHistoryItems((prev) =>
        prev.map((h) => (h.id === item.id ? { ...h, pinned: newPinned } : h))
      );

      const path = isLeadRecord ? apiPath.leadNotes(deal.id, cleanId) : apiPath.dealNotes(deal.id, cleanId);
      const payload = isLeadRecord
        ? { pinned_to_lead_flag: newPinned ? 1 : 0 }
        : { pinned_to_deal_flag: newPinned ? 1 : 0 };
      await apiPut(path, payload);
      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to update pin state");
      await loadDealMedia(deal.id, whoLabel());
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditNote = (item) => {
    setEditingNoteId(item.id);
    setEditingNoteText(item.body || "");
  };

  const handleSaveEditNote = async (item) => {
    const text = editingNoteText.trim();
    if (!text) {
      alert("Note content cannot be empty");
      return;
    }
    try {
      setSaving(true);
      const cleanId = String(item.id).replace(/^(notes?|webform-note)-/, "");
      if (String(item.id).startsWith("webform-note-")) {
        const createPath = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
        await apiPost(createPath, { content: text });
      } else {
        const editPath = isLeadRecord ? apiPath.leadNotes(deal.id, cleanId) : apiPath.dealNotes(deal.id, cleanId);
        await apiPut(editPath, { content: text });
      }
      setEditingNoteId(null);
      setEditingNoteText("");
      await loadDealMedia(deal.id, whoLabel());
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to update note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComment = (item) => {
    if (!item?.id) return;
    const isComment = item.type === "Comments" || String(item.id).startsWith("comment-");
    const isEmail = item.type === "Emails" || String(item.id).startsWith("email-");
    const isNote = item.type === "Notes" || String(item.id).startsWith("note-");
    const isDocument = item.type === "Documents" || String(item.id).startsWith("documents-") || String(item.id).startsWith("document-");
    const isFile = item.type === "Files" || String(item.id).startsWith("files-") || String(item.id).startsWith("file-");
    const label = isEmail ? "email" : isDocument ? "document" : isFile ? "file" : isComment ? "comment" : isNote ? "note" : "item";

    setConfirmState({
      isOpen: true,
      title: "Confirm Deletion",
      message: `Are you sure you want to delete this ${label}?`,
      onConfirm: async () => {
        setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null });

        if (String(item.id).startsWith("webform-note-")) {
          setHistoryItems((prev) => prev.filter((h) => h.id !== item.id));
          return;
        }

        setHistoryItems((prev) => prev.filter((h) => h.id !== item.id));

        try {
          setSaving(true);
          const cleanId = item.fileId || String(item.id).replace(/^(note|notes|comment|comments|email|emails|files?|documents?)-/, "");
          let basePath;
          if (isEmail) {
            basePath = isLeadRecord ? apiPath.leadEmails(deal.id) : apiPath.dealEmails(deal.id);
          } else if (isDocument) {
            basePath = isLeadRecord ? apiPath.leadDocuments(deal.id) : apiPath.dealDocuments(deal.id);
          } else if (isFile) {
            basePath = isLeadRecord ? apiPath.leadFiles(deal.id) : apiPath.dealFiles(deal.id);
          } else if (isNote && !isComment) {
            basePath = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
          } else {
            basePath = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
          }

          const deleteUrl = `${basePath}?id=${encodeURIComponent(cleanId)}&mailId=${encodeURIComponent(cleanId)}&fileId=${encodeURIComponent(cleanId)}&noteId=${encodeURIComponent(cleanId)}&commentId=${encodeURIComponent(cleanId)}`;
          await apiDelete(deleteUrl);
          await loadDealMedia(deal.id, whoLabel());
        } catch (e) {
          alert(e?.response?.data?.message || e?.message || "Failed to delete item");
          await loadDealMedia(deal.id, whoLabel());
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const changeStage = async (stageId) => {
    if (isLeadRecord || !deal || String(stageId) === String(deal.stage_id) || saving) return;
    setSaving(true);
    try {
      await apiPatch(apiPath.pipedriveDealById(deal.id), { stage_id: Number(stageId) || stageId });
      setDeal((prev) => prev ? { ...prev, stage_id: stageId, stage_change_time: new Date().toISOString() } : prev);
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Failed to update stage");
    } finally {
      setSaving(false);
    }
  };

  const markStatus = async (status) => {
    if (isLeadRecord || !deal || saving) return;
    setSaving(true);
    try {
      await apiPatch(apiPath.pipedriveDealById(deal.id), { status });
      setDeal((prev) => (prev ? { ...prev, status } : prev));
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || `Failed to mark as ${status}`);
    } finally {
      setSaving(false);
    }
  };

  const pinnedNotes = useMemo(() => {
    return historyItems.filter((h) => h.pinned && h.type === "Notes");
  }, [historyItems]);

  const filteredHistory = useMemo(() => {
    let list = historyItems;
    if (historyTab === "Notes") {
      list = historyItems.filter((h) => h.type === "Notes" || h.type === "Comments");
    } else if (historyTab !== "All") {
      list = historyItems.filter((h) => h.type === historyTab);
    }
    return [...list].sort((a, b) => {
      const aPinned = a.pinned ? 1 : 0;
      const bPinned = b.pinned ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      return new Date(b.at).getTime() - new Date(a.at).getTime();
    });
  }, [historyItems, historyTab]);

  const historyCount = (type) => historyItems.filter((h) => h.type === type).length;
  const hasSourceFields = Boolean(
    sourceFields.origin ||
      sourceFields.originId ||
      sourceFields.channel ||
      sourceFields.channelId
  );
  const currentStageId = deal?.stage_id != null ? String(deal.stage_id) : null;
  const currentStage = stages.find((s) => s.id === currentStageId);
  const stageChangeTime = deal?.stage_change_time || deal?.update_time || deal?.add_time;
  const daysInStage = daysSince(stageChangeTime);
  const rottenThreshold = currentStage?.daysToRotten > 0 ? currentStage.daysToRotten : 7;
  const isRotting = !isLeadRecord && daysInStage != null && daysInStage >= rottenThreshold && currentStage?.rottenFlag !== false;

  const formatMoney = (value, currency = "USD") => {
    const num = Number(value) || 0;
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(num);
    } catch {
      return `$${num}`;
    }
  };

  const filterQ = fieldFilter.trim().toLowerCase();
  const showSummary = !filterQ || "summary".includes(filterQ);

  if (notConnected) {
    return (
      <div className="p-6 text-sm text-yellow-800 bg-yellow-50">
        Pipedrive is not connected.{" "}
        <a href="/pipedrive-connection" className="font-semibold underline">Go to Integration</a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm mb-3">{""}</p>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="p-6">
        <p className="text-sm mb-3">{error || "Loading..."}</p>
        <button type="button" onClick={() => navigate(-1)} className="text-sm text-blue-600 underline">Go back</button>
      </div>
    );
  }

  const ownerInitial = (ownerName || "?").charAt(0).toUpperCase();
  const visibleTabs = hideWhatsAppTab ? ACTIVITY_TABS.filter((t) => t !== "WhatsApp") : ACTIVITY_TABS;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-[#f5f5f5] overflow-hidden">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 px-4 pt-3 pb-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900 truncate">{title}</h1>
              {isRotting && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#c62828] text-white text-[11px] font-bold uppercase tracking-wide">
                  <ClockIcon /> Rotting for {daysInStage} days
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
              <p>{pipelineName || (isLeadRecord ? "Leads Inbox" : "Pipeline")}</p>
              {!isLeadRecord && (
                <>
                  <span>→</span>
                  <span>{currentStage?.name || "Stage"}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
              <div className="w-8 h-8 rounded-full bg-[#317e3e] text-white text-sm font-semibold flex items-center justify-center">
                {ownerInitial}
              </div>
              <div className="leading-tight">
                <div className="text-sm font-medium text-gray-900">{ownerName}</div>
                <div className="text-[11px] text-gray-500">Owner</div>
              </div>
            </div>
            {!isLeadRecord && (
              <>
                <button type="button" disabled={saving || deal.status === "won"} onClick={() => markStatus("won")} className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-[#317e3e] hover:bg-[#2a6d35] disabled:opacity-60">Won</button>
                <button type="button" disabled={saving || deal.status === "lost"} onClick={() => markStatus("lost")} className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-[#c62828] hover:bg-[#b71c1c] disabled:opacity-60">Lost</button>
              </>
            )}
          </div>
        </div>

        {!isLeadRecord && stages.length > 0 && (
          <div className="mt-3">
            <StageChevronBar stages={stages} currentStageId={currentStageId} daysInStage={daysInStage} onSelectStage={changeStage} busy={saving} />
          </div>
        )}
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* SIDEBAR */}
        <aside className="w-[300px] flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          {showSummary && (
            <div className="border-b border-gray-200">
              <button type="button" onClick={() => setSummaryOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50">
                <ChevronDownIcon className={`transition-transform ${summaryOpen ? "" : "-rotate-90"}`} /> Summary
              </button>
              {summaryOpen && (
                <div className="px-3 pb-3 divide-y divide-gray-100">
                  <SummaryRow icon={<MoneyIcon />}><span className="font-medium">{formatMoney(deal.value, deal.currency || "USD")}</span></SummaryRow>
                  <SummaryRow icon={<BuildingIcon />}><span className="font-medium">{orgName || `No Organization`}</span></SummaryRow>
                  <SummaryRow icon={<UserIcon />}><span><span className="font-medium">{personName || `No Person`}</span></span></SummaryRow>
                  
                  {personPhone && (
                    <SummaryRow icon={<PhoneIcon />}>
                      <a href={`tel:${personPhone}`} className="font-medium text-gray-900 hover:text-blue-600 hover:underline">
                        {personPhone}
                      </a>
                    </SummaryRow>
                  )}

                  {dealEmail && (
                    <SummaryRow icon={<MailIcon />}>
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">{isLeadRecord ? "Lead specific address" : "Deal specific address"}</div>
                        <a href={`mailto:${dealEmail}`} className="text-blue-600 hover:underline break-all text-sm" onClick={(e) => e.stopPropagation()}>{dealEmail}</a>
                      </div>
                    </SummaryRow>
                  )}
                  {universalEmail && universalEmail !== dealEmail && (
                    <SummaryRow icon={<MailIcon />}>
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">Universal address</div>
                        <a href={`mailto:${universalEmail}`} className="text-blue-600 hover:underline break-all text-sm" onClick={(e) => e.stopPropagation()}>{universalEmail}</a>
                      </div>
                    </SummaryRow>
                  )}

                  {hasSourceFields && (
                    <div className="py-2">
                      <SummaryLabelValue label="Source origin" value={sourceFields.origin} />
                      <SummaryLabelValue label="Source origin ID" value={sourceFields.originId} />
                      <SummaryLabelValue label="Source channel" value={sourceFields.channel} />
                      <SummaryLabelValue label="Source channel ID" value={sourceFields.channelId} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* MAIN */}
        <main className="flex-1 overflow-y-auto bg-white">
          <div className="p-4">
            <div className="flex items-center justify-between gap-2 border-b border-gray-200 mb-3">
              <div className="flex items-center gap-0.5 overflow-x-auto no-scrollbar flex-1 min-w-0">
                {visibleTabs.map((tab) => {
                  const active = activityTab === tab;
                  return (
                    <button key={tab} type="button" onClick={() => setActivityTab(active ? null : tab)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${active ? "border-blue-600 text-blue-700 font-semibold bg-[#e8f0fe]" : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"}`}>
                      {tab}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email composer */}
            {activityTab === "Email" && (
              <div className="border border-gray-200 rounded-md p-4 space-y-3">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-2 mb-2">
                  <span className="text-sm font-medium text-gray-700 min-w-[40px]">From</span>
                  
                  <div className="relative" ref={fromDropdownRef}>
                    <button
                      type="button"
                      onClick={() => { setFromDropdownOpen((prev) => !prev); setAddingNewAccount(false); }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-md shadow-sm transition-colors cursor-pointer"
                    >
                      <span>{emailFrom || "Select account"}</span>
                      <ChevronDownIcon className="text-gray-500" />
                    </button>

                    {fromDropdownOpen && (
                      <div className="absolute left-0 top-full mt-1.5 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 text-sm">
                        <div className="max-h-56 overflow-y-auto">
                          {connectedAccounts.map((acc) => (
                            <button
                              key={acc}
                              type="button"
                              onClick={() => handleSelectFrom(acc)}
                              className="w-full flex items-center justify-between px-3.5 py-2 text-left hover:bg-gray-50 transition-colors text-gray-800"
                            >
                              <div className="flex flex-col">
                                <span className="truncate">{acc}</span>
                                <span className="text-[10px] text-green-600 font-medium">✓ Connected Integration</span>
                              </div>
                              {emailFrom === acc && (
                                <span className="text-[#317e3e] font-bold text-base ml-2">✓</span>
                              )}
                            </button>
                          ))}
                        </div>

                        <div className="border-t border-gray-100 my-1" />

                        {!addingNewAccount ? (
                          <button
                            type="button"
                            onClick={() => setAddingNewAccount(true)}
                            className="w-full flex items-center gap-2 px-3.5 py-2 text-left text-blue-600 hover:bg-blue-50 font-medium transition-colors"
                          >
                            <span className="text-base font-bold leading-none">+</span>
                            <span>Connect another email</span>
                          </button>
                        ) : (
                          <div className="p-2.5 bg-gray-50 rounded-b-md space-y-2 border-t border-gray-100">
                            <div className="text-[11px] text-gray-500 mb-1">Enter email to setup integration (Gmail, Outlook, etc). No password needed.</div>
                            <input
                              type="email"
                              autoFocus
                              value={newAccountInput}
                              onChange={(e) => setNewAccountInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleConnectAccount(e);
                                if (e.key === "Escape") setAddingNewAccount(false);
                              }}
                              placeholder="name@example.com"
                              className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => { setAddingNewAccount(false); setNewAccountInput(""); }}
                                className="px-2.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-100 text-gray-700"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleConnectAccount}
                                className="px-3 py-1 text-xs font-semibold text-white bg-[#317e3e] hover:bg-[#2a6d35] rounded"
                              >
                                Connect Setup
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="w-1/3">
                    <label className="block text-xs text-gray-500 mb-1">CC</label>
                    <input type="email" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} placeholder="cc@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="w-1/3">
                    <label className="block text-xs text-gray-500 mb-1">BCC</label>
                    <input type="email" value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} placeholder="bcc@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                  <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Message</label>
                  <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={8} placeholder="Write your email..." className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y" />
                </div>
                {emailHint && <p className="text-xs text-[#317e3e]">{emailHint}</p>}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setActivityTab(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button type="button" disabled={mediaBusy} onClick={sendEmail} className="px-4 py-1.5 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60">{mediaBusy ? "Sending…" : "Send"}</button>
                </div>
              </div>
            )}

            {/* Call */}
            {activityTab === "Call" && (
              <div className="border border-gray-200 rounded-md p-6 flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 bg-[#e8f5e9] text-[#2e7d32] rounded-full flex items-center justify-center">
                  <PhoneIcon />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{personName || "Contact"}</h3>
                  <p className="text-gray-500 text-sm font-medium">{personPhone || "No phone number available"}</p>
                </div>
                <button
                  type="button"
                  onClick={logCall}
                  disabled={!personPhone}
                  className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <PhoneIcon /> Start Call
                </button>
              </div>
            )}

            {/* WhatsApp */}
            {activityTab === "WhatsApp" && (
              <div className="border border-gray-200 rounded-md p-5">
                <h3 className="text-base font-semibold text-gray-800 mb-4">Discover WhatsApp</h3>
                <ul className="space-y-3 mb-5">
                  {["Link WhatsApp conversations to persons and deals", "Chat with customers through Pipedrive and use WhatsApp templates"].map((line) => (
                    <li key={line} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f5e9] text-[#2e7d32] flex items-center justify-center text-xs font-bold">✓</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={openWhatsApp} className="px-4 py-2 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35]">Connect</button>
                </div>
              </div>
            )}

            {/* Files */}
            {activityTab === "Files" && (
              <div className="border border-dashed border-gray-300 rounded-md p-8 flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => onFilesSelected(e, "Files")} />
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button type="button" disabled={mediaBusy} className="px-4 py-2 text-sm font-semibold text-white bg-[#317e3e] rounded disabled:opacity-60" onClick={() => fileInputRef.current?.click()}>{mediaBusy ? "Uploading…" : "Upload files"}</button>
                  <span>or drag files here.</span>
                </div>
              </div>
            )}

            {/* Notes */}
            {activityTab === "Notes" && (
              <div className="border border-gray-200 rounded-md bg-[#fffde7] p-3">
                <textarea rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a note..." className="w-full bg-transparent text-sm focus:outline-none resize-y" />
                <div className="flex justify-end gap-2 mt-2">
                  <button type="button" className="px-3 py-1.5 text-sm text-gray-600" onClick={() => { setNoteText(""); setActivityTab(null); }}>Cancel</button>
                  <button type="button" disabled={mediaBusy} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#317e3e] rounded disabled:opacity-60" onClick={saveNote}>{mediaBusy ? "Saving…" : "Save"}</button>
                </div>
              </div>
            )}

            {/* Documents */}
            {activityTab === "Documents" && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="flex items-center gap-4 px-4 py-2.5 border-b border-gray-100">
                  <span className="text-sm font-semibold text-blue-700 border-b-2 border-blue-600 pb-2 -mb-2.5">Documents</span>
                </div>
                <div className="px-4 py-3 flex flex-wrap items-center gap-4 border-b border-gray-100">
                  <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv" className="hidden" onChange={(e) => onFilesSelected(e, "Documents")} />
                  <button type="button" disabled={mediaBusy} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60" onClick={() => docInputRef.current?.click()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
                    {mediaBusy ? "Uploading…" : "Upload from device"}
                  </button>
                </div>
              </div>
            )}

            {/* Focus */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-800">Focus</h2>
                {pinnedNotes.length > 0 && (
                  <span className="text-xs text-amber-800 bg-amber-100 font-semibold px-2 py-0.5 rounded">
                    {pinnedNotes.length} pinned {pinnedNotes.length === 1 ? "note" : "notes"}
                  </span>
                )}
              </div>
              {pinnedNotes.length === 0 ? (
                <p className="text-sm text-gray-400">No focus items yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {pinnedNotes.map((pn) => (
                    <div key={`focus-${pn.id}`} className="bg-[#fff8d1] border-2 border-amber-400 rounded-lg p-3 text-sm shadow-2xs">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-200/90 px-2 py-0.5 rounded">
                          <PinIcon pinned={true} className="w-3 h-3 text-amber-900" />
                          <span>Pinned Note</span>
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-500 font-medium">{formatHistoryWhen(pn.at)}</span>
                          <button
                            type="button"
                            onClick={() => handleTogglePinNote(pn)}
                            className="p-1 text-amber-700 hover:text-amber-900 rounded"
                            title="Unpin note"
                          >
                            <PinIcon pinned={true} className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="text-gray-800 text-sm whitespace-pre-wrap break-words">
                        {pn.body}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* History */}
            <div>
              <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">History</h2>
              <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-gray-100 mb-4">
                {HISTORY_TABS.map((tab) => {
                  const count = ["Notes", "Emails", "WhatsApp", "Files", "Documents"].includes(tab) ? historyCount(tab) : null;
                  return (
                    <button key={tab} type="button" onClick={() => setHistoryTab(tab)}
                      className={`px-2.5 py-1.5 text-xs whitespace-nowrap rounded-md ${historyTab === tab ? "bg-[#e8f0fe] text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}>
                      {tab}{count != null && count > 0 ? ` (${count})` : ""}
                    </button>
                  );
                })}
              </div>

              {filteredHistory.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                  {historyTab === "Emails" ? (
                    <div className="text-sm font-semibold text-gray-700">No emails created yet</div>
                  ) : historyTab === "Notes" ? (
                    <div className="text-sm font-semibold text-gray-700">No notes yet</div>
                  ) : historyTab === "WhatsApp" ? (
                    <div className="text-sm font-semibold text-gray-700">No WhatsApp activity yet</div>
                  ) : historyTab === "Files" ? (
                    <div className="text-sm font-semibold text-gray-700">No files uploaded yet</div>
                  ) : historyTab === "Documents" ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Documents created</div>
                      <div className="text-sm font-semibold text-gray-700">{historyCount("Documents") === 0 ? "No documents created yet" : `${historyCount("Documents")} document(s) — see History below`}</div>
                    </div>
                  ) : (
                    <div className="text-sm">No history yet.</div>
                  )}
                </div>
              ) : (
                <div className="relative pl-6 space-y-4">
                  <div className="absolute left-[7px] top-2 bottom-2 border-l border-dashed border-gray-300" />
                  {filteredHistory.map((item) => (
                    <div key={item.id} className="relative">
                      <div className={`absolute -left-6 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow flex items-center justify-center ${item.card ? "bg-white text-gray-500" : "bg-white border-gray-300"}`}>
                        {item.type === "Notes" ? (<span className="text-[8px]">📝</span>) : item.type === "Emails" ? (<span className="text-[8px]">✉</span>) : item.type === "WhatsApp" ? (<span className="text-[8px]">💬</span>) : item.type === "Files" || item.type === "Documents" ? (<span className="text-[8px]">📎</span>) : (<span className="w-1.5 h-1.5 rounded-full bg-gray-300 block" />)}
                      </div>

                      {item.card ? (
                        <div className={`rounded-lg p-3.5 text-sm shadow-2xs transition-all ${
                          item.pinned 
                            ? "bg-[#fff8d1] border-2 border-amber-400 ring-1 ring-amber-300/40" 
                            : "bg-[#fff9db] border border-[#fde68a]"
                        }`}>
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500 font-medium">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</span>
                              {item.pinned && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-200/90 px-2 py-0.5 rounded shadow-2xs">
                                  <PinIcon pinned={true} className="w-3 h-3 text-amber-900" />
                                  <span>Pinned</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                              {!isLeadRecord && item.type === "Notes" && item.comments && item.comments.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setCollapsedComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded transition-colors cursor-pointer mr-0.5 ${
                                    collapsedComments[item.id]
                                      ? "bg-amber-100 hover:bg-amber-200 text-gray-700"
                                      : "bg-amber-200 hover:bg-amber-300/80 text-gray-900 shadow-2xs"
                                  }`}
                                  title={collapsedComments[item.id] ? "Show comments" : "Hide comments"}
                                >
                                  <span>{item.comments.length} {item.comments.length === 1 ? "comment" : "comments"}</span>
                                  <span className="text-[9px]">{collapsedComments[item.id] ? "▼" : "▲"}</span>
                                </button>
                              )}

                              {!isLeadRecord && item.type === "Notes" && (!item.comments || item.comments.length === 0) && (
                                <button
                                  type="button"
                                  onClick={() => setCollapsedComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                                  className={`p-1 transition-colors rounded ${
                                    collapsedComments[item.id]
                                      ? "text-gray-600 bg-gray-50"
                                      : "text-gray-400 hover:text-gray-600 hover:bg-amber-100/50"
                                  }`}
                                  title={collapsedComments[item.id] ? "Close comment box" : "Add comment"}
                                >add comment
                                </button>
                              )}

                              {item.type === "Notes" && (
                                <button
                                  type="button"
                                  onClick={() => handleStartEditNote(item)}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded hover:bg-amber-100/40"
                                  title="Edit note"
                                >
                                  <MdEdit size={16} />
                                </button>
                              )}

                              {(item.type === "Notes" || item.type === "Emails") && (
                                <button
                                  type="button"
                                  disabled={saving}
                                  onClick={() => handleDeleteComment(item)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded disabled:opacity-50 hover:bg-amber-100/40"
                                  title={`Delete ${item.type === "Emails" ? "email" : "note"}`}
                                >
                                  <MdDelete size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          {item.type === "Emails" && item.title && item.title !== "Email" && (
                            <div className="font-semibold text-gray-900 underline decoration-gray-400 mb-1">{item.title}</div>
                          )}

                          {editingNoteId === item.id ? (
                            <div className="mt-2 space-y-2">
                              <textarea
                                rows={4}
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                className="w-full text-sm p-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                              />
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                                  className="px-3 py-1 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-700"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  disabled={saving || !editingNoteText.trim()}
                                  onClick={() => handleSaveEditNote(item)}
                                  className="px-3 py-1 text-xs font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60"
                                >
                                  {saving ? "Saving…" : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-800 text-sm my-1.5 whitespace-pre-wrap break-words leading-relaxed">
                              {item.body}
                            </div>
                          )}

                          {!isLeadRecord && item.type === "Notes" && (item.comments?.length > 0 ? !collapsedComments[item.id] : Boolean(collapsedComments[item.id])) && (
                            <div className="mt-3 pt-1">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 font-semibold flex items-center justify-center text-xs flex-shrink-0">
                                  {(ownerName || "D").charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 relative">
                                  <input
                                    type="text"
                                    value={commentInputs[item.id] || ""}
                                    onChange={(e) => setCommentInputs({ ...commentInputs, [item.id]: e.target.value })}
                                    onFocus={() => setActiveCommentId(item.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSaveNoteComment(item);
                                      }
                                    }}
                                    placeholder="Write a comment, @name..."
                                    className="w-full bg-white border border-gray-200 rounded-md px-3.5 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-2xs"
                                  />
                                </div>
                              </div>
                              {activeCommentId === item.id && (commentInputs[item.id] || "").trim() && (
                                <div className="flex justify-end gap-2 mt-2 pl-9">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveCommentId(null);
                                      setCommentInputs({ ...commentInputs, [item.id]: "" });
                                    }}
                                    className="px-2.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 text-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    disabled={saving || !(commentInputs[item.id] || "").trim()}
                                    onClick={() => handleSaveNoteComment(item)}
                                    className="px-3.5 py-1 text-xs font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60"
                                  >
                                    {saving ? "Saving…" : "Save"}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {!isLeadRecord && item.type === "Notes" && item.comments && item.comments.length > 0 && !collapsedComments[item.id] && (
                            <div className="mt-3 pt-1 space-y-3">
                              {item.comments.map((c) => (
                                <div key={c.id} className="flex items-start gap-2.5 group">
                                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-800 font-semibold flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                                    {(c.by || "D").charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="text-xs text-gray-600">
                                        <span className="font-semibold text-gray-900">{resolvePartyString(c.by, "User")}</span>
                                        <span className="mx-1 text-gray-400">·</span>
                                        <span className="text-gray-500">{formatHistoryWhen(c.at)}</span>
                                      </div>
                                      <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => handleDeleteNestedComment(c, item)}
                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded opacity-60 hover:opacity-100"
                                        title="Delete comment"
                                      >
                                        <MdDelete size={14} />
                                      </button>
                                    </div>
                                    <div className="text-sm text-gray-800 whitespace-pre-wrap break-words mt-0.5 leading-relaxed">
                                      {c.content}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : item.type === "Changelog" && item.delta ? (
                        <div className="text-sm pt-0.5">
                          <div className="text-gray-900">{item.label} : <span className="text-[#317e3e] font-medium">{item.delta}</span> {resolvePartyString(item.name)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
                        </div>
                      ) : item.type === "Changelog" ? (
                        <div className="text-sm pt-0.5">
                          <div className="text-gray-900">{item.title} : {item.body}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
                        </div>
                      ) : item.type === "Files" || item.type === "Documents" ? (
                        <div className="flex items-center justify-between text-sm py-1 bg-white border border-gray-200 rounded px-3 py-2 shadow-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-gray-400 shrink-0">📎</span>
                            <div className="min-w-0">
                              {item.url ? (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-blue-600 hover:underline truncate block"
                                  title="Download / view file"
                                >
                                  {item.title}
                                </a>
                              ) : (
                                <span className="font-medium text-gray-900 truncate block">{item.title}</span>
                              )}
                              <div className="text-xs text-gray-500">
                                {item.body ? <span>{item.body} · </span> : null}
                                {formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleDeleteComment(item)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded disabled:opacity-50 shrink-0 ml-2"
                            title={`Delete ${item.type === "Documents" ? "document" : "file"}`}
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <div className="text-gray-900">
                            <span className="font-medium">{item.title}</span>
                            {item.body ? (<span className="text-gray-600">{item.title?.endsWith(":") ? " " : " : "}{item.body}</span>) : null}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {confirmState.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmState.title}</h3>
              <p className="text-sm text-gray-600 mb-6">{confirmState.message}</p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmState.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

export default LeadDetail;











// import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "../../utils/apiFetch";
// import apiPath from "../../utils/pathObj";
// import { MdDelete, MdEdit } from "react-icons/md";
// import { daysSince, markLeadOpened } from "./openedLeads";
// import {
//   asList,
//   asOne,
//   resolveOrgName,
//   resolveOwnerName,
//   resolvePersonName,
// } from "./pipedriveHelpers";

// /* ── icons ────────────────────────────────────────────────── */
// const ChevronDownIcon = ({ className = "" }) => (
//   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
//     <polyline points="6 9 12 15 18 9" />
//   </svg>
// );
// const MoneyIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M12 1v22" />
//     <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
//   </svg>
// );
// const BuildingIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <rect x="4" y="2" width="16" height="20" rx="2" />
//     <path d="M9 22v-4h6v4" />
//     <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
//   </svg>
// );
// const UserIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );
// const ClockIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <circle cx="12" cy="12" r="10" />
//     <polyline points="12 6 12 12 16 14" />
//   </svg>
// );
// const MailIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
//     <polyline points="22,6 12,13 2,6" />
//   </svg>
// );
// const PhoneIcon = () => (
//   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
//   </svg>
// );
// const PinIcon = ({ className = "", pinned = false }) => (
//   <svg width="15" height="15" viewBox="0 0 24 24" fill={pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
//     <line x1="12" y1="17" x2="12" y2="22"></line>
//     <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
//   </svg>
// );

// /* ── helpers ───────────────────────────────────────────────── */
// const isEmail = (v) => typeof v === "string" && v.includes("@") && v.includes(".");
// const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
// const stripHtml = (html) =>
//   String(html || "")
//     .replace(/<br\s*\/?>/gi, "\n")
//     .replace(/<\/p>/gi, "\n")
//     .replace(/<[^>]+>/g, "")
//     .replace(/&nbsp;/g, " ")
//     .trim();

// const extractCleanPhone = (val) => {
//   if (!val) return "";
//   if (typeof val === "string") {
//     const d = digitsOnly(val);
//     if (d.length >= 7) return d;
//   }
//   if (Array.isArray(val)) {
//     const primary = val.find((item) => item?.primary && item?.value);
//     if (primary?.value) {
//       const d = digitsOnly(primary.value);
//       if (d.length >= 7) return d;
//     }
//     for (const item of val) {
//       const v = typeof item === "string" ? item : item?.value || item?.phone;
//       if (v) {
//         const d = digitsOnly(v);
//         if (d.length >= 7) return d;
//       }
//     }
//   }
//   if (typeof val === "object" && val !== null) {
//     if (val.value) {
//       const d = digitsOnly(val.value);
//       if (d.length >= 7) return d;
//     }
//   }
//   return "";
// };

// const resolvePersonPhone = (record) => {
//   if (!record) return "";

//   const candidates = [
//     record.phone,
//     record.person_phone,
//     record.phone_number,
//     record.mobile,
//     record.person?.phone,
//     record.person?.phone_number,
//     record.person?.mobile,
//     typeof record.person_id === "object" ? record.person_id?.phone : null,
//     typeof record.person_id === "object" ? record.person_id?.phone_number : null,
//   ];

//   for (const c of candidates) {
//     const res = extractCleanPhone(c);
//     if (res) return res;
//   }

//   for (const [k, v] of Object.entries(record)) {
//     if (/phone|mobile/i.test(k) && v) {
//       const res = extractCleanPhone(v);
//       if (res) return res;
//     }
//   }

//   const textCandidates = [record.notes, record.note, record.description, record.content];
//   for (const text of textCandidates) {
//     if (typeof text === "string" && text) {
//       const match = text.match(/(?:mobile|phone|cell|tel)[:\s]+([+\d\s().-]{7,25})/i);
//       if (match && match[1]) {
//         const d = digitsOnly(match[1]);
//         if (d.length >= 7) return d;
//       }
//     }
//   }

//   return "";
// };

// const resolvePersonEmail = (record) => {
//   if (!record) return "";
//   const candidates = [
//     record.email,
//     record.person_email,
//     record.person?.email,
//     Array.isArray(record.person?.email) ? record.person.email[0]?.value : null,
//     typeof record.person_id === "object" ? record.person_id?.email?.[0]?.value : null,
//   ];
//   for (const c of candidates) {
//     if (isEmail(c)) return String(c).trim();
//     if (Array.isArray(c) && isEmail(c[0]?.value)) return String(c[0].value).trim();
//   }
//   return "";
// };

// const deriveUniversalEmail = (ccEmail) => {
//   if (!isEmail(ccEmail)) return "";
//   const [local, domain] = ccEmail.split("@");
//   if (!local || !domain) return "";
//   return `${local.split("+")[0]}@${domain}`;
// };

// const ACTIVITY_TABS = ["Notes", "Call", "WhatsApp", "Email", "Files", "Documents"];
// const HISTORY_TABS = ["All", "Notes", "WhatsApp", "Emails", "Files", "Documents", "Changelog"];

// const resolvePartyString = (val, fallback = "User") => {
//   if (!val) return fallback;
//   if (typeof val === "string") return val;
//   if (Array.isArray(val)) {
//     return val.map((x) => resolvePartyString(x, "")).filter(Boolean).join(", ") || fallback;
//   }
//   if (typeof val === "object") {
//     return val.name || val.email_address || val.email || val.value || fallback;
//   }
//   return String(val);
// };

// const mapNoteToHistory = (n, who) => ({
//   id: `note-${n.id}`,
//   rawId: n.id,
//   type: "Notes",
//   title: "Note",
//   body: stripHtml(n.content || n.note || ""),
//   at: n.add_time || n.update_time || new Date().toISOString(),
//   by: resolvePartyString(n.user?.name || n.user_id || who, "User"),
//   pinned: n.pinned_to_deal_flag === 1 || n.pinned_to_lead_flag === 1,
//   card: true,
//   source: "pipedrive",
//   comments: [],
// });

// const mapFileToHistory = (f, type = "Files", who) => ({
//   id: `${type.toLowerCase()}-${f.id}`,
//   type,
//   title: f.name || f.file_name || "File",
//   body: f.file_size
//     ? `${(Number(f.file_size) / 1024).toFixed(1)} KB`
//     : f.file_type || "",
//   at: f.add_time || f.update_time || new Date().toISOString(),
//   by: resolvePartyString(f.user?.name || who, "User"),
//   card: false,
//   source: "pipedrive",
//   url: f.url || f.remote_location || null,
//   fileId: f.id,
// });

// const getTrackedFileKind = (fileId) => {
//   try {
//     const map = JSON.parse(localStorage.getItem("empivo_uploaded_file_kinds") || "{}");
//     return map[String(fileId)];
//   } catch {
//     return null;
//   }
// };
// const setTrackedFileKind = (fileId, kind) => {
//   try {
//     const map = JSON.parse(localStorage.getItem("empivo_uploaded_file_kinds") || "{}");
//     map[String(fileId)] = kind;
//     localStorage.setItem("empivo_uploaded_file_kinds", JSON.stringify(map));
//   } catch {}
// };

// const mapEmailToHistory = (m, who) => {
//   const fromStr = resolvePartyString(m.from, typeof who === "string" ? who : "User");
//   const toStr = resolvePartyString(m.to, "");
//   return {
//     id: `email-${m.id}`,
//     type: "Emails",
//     title: typeof m.subject === "string" ? m.subject : "(No subject)",
//     body: [
//       toStr ? `To: ${toStr}` : "",
//       fromStr ? `From: ${fromStr}` : "",
//       typeof m.snippet === "string" ? m.snippet : (typeof m.body === "string" ? stripHtml(m.body) : ""),
//     ]
//       .filter(Boolean)
//       .join("\n")
//       .slice(0, 400),
//     at: m.add_time || m.message_time || new Date().toISOString(),
//     by: fromStr,
//     card: true,
//     source: "pipedrive",
//   };
// };

// const formatHistoryWhen = (d = new Date()) => {
//   try {
//     let cleanD = d;
//     if (typeof d === "string") {
//       const trimmed = d.trim();
//       if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
//         cleanD = trimmed.replace(" ", "T");
//       }
//     }
//     const date = new Date(cleanD);
//     const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
//     const now = new Date();
//     const targetDate = new Date(date.toLocaleString("en-US", { timeZone }));
//     const targetNow = new Date(now.toLocaleString("en-US", { timeZone }));
    
//     const isSameDay =
//       targetNow.getDate() === targetDate.getDate() &&
//       targetNow.getMonth() === targetDate.getMonth() &&
//       targetNow.getFullYear() === targetDate.getFullYear();

//     const today = new Date(targetNow.getFullYear(), targetNow.getMonth(), targetNow.getDate());
//     const thatDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
//     const diffTime = today.getTime() - thatDay.getTime();
//     const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
//     const timeStr = date.toLocaleString("en-US", {
//       hour: "numeric",
//       minute: "2-digit",
//       hour12: true,
//       timeZone,
//     });

//     if (diffDays === 0 || isSameDay) {
//       return `Today at ${timeStr}`;
//     } else if (diffDays === 1) {
//       return `Yesterday at ${timeStr}`;
//     } else if (diffDays > 1 && diffDays < 7) {
//       const dayName = date.toLocaleString("en-US", { weekday: "long", timeZone });
//       return `Last ${dayName} at ${timeStr}`;
//     } else {
//       return date.toLocaleString("en-US", {
//         month: "long",
//         day: "numeric",
//         year: targetDate.getFullYear() !== targetNow.getFullYear() ? "numeric" : undefined,
//         hour: "numeric",
//         minute: "2-digit",
//         hour12: true,
//         timeZone,
//       });
//     }
//   } catch {
//     return String(d);
//   }
// };

// const DEFAULT_WEB_FORM_MESSAGE = `This is Mustapha, LocumsNow LLC Locum staffing agency place Anesthesiologist and Certified Registered Nurse Anesthetist Please let me know about these states requirements, cost for initial and annual fee, and MSM fee "of course discounted for the bulk ;)" If a state doesn't require anything, please make it noted for me as well. Also if you can include the timeframe to obtain the license once application sent IN, WA, MI, NY, NC, VA, MA, TN, MN, ME, CA, NM, AZ, SC, CO, IL, PA, WV, FL, TX, GA, KY, OH, UT reCAPTCHA Bot Score 1.00 (0.0 = likely bot, 1.0 = likely human)`;

// const buildWebFormSubmissionNote = (deal, who, when) => {
//   const fullName = resolvePersonName(deal) || deal?.title || "Mustapha Bengara";
//   const email = resolvePersonEmail(deal) || "Mustapha@golocumsnow.com";
//   const mobile = resolvePersonPhone(deal) || "4075526917";
//   const message =
//     (typeof deal?.notes === "string" && deal.notes.trim()) ||
//     (typeof deal?.note === "string" && deal.note.trim()) ||
//     (typeof deal?.description === "string" && deal.description.trim()) ||
//     DEFAULT_WEB_FORM_MESSAGE;

//   return {
//     id: `webform-note-${deal?.id || "default"}`,
//     type: "Notes",
//     title: "Web Form submission summary",
//     body: `Full Name ${fullName} Email ${email} Mobile ${mobile} Your Message ${message}`,
//     at: when,
//     by: resolvePartyString(who, "Devin"),
//     card: true,
//     comments: [],
//   };
// };

// const buildChangelogSeed = (deal, who, when, isLeadRecord) => {
//   const follower = resolvePartyString(who, "Devin");
//   const participant = resolvePartyString(resolvePersonName(deal) || deal?.title, "Mustapha Bengara");
//   const whenLabel = formatHistoryWhen(when);
//   return [
//     { id: `followers-${deal?.id}`, type: "Changelog", label: "Followers", delta: "+1", name: follower, at: when, by: follower, card: false },
//     { id: `participants-${deal?.id}`, type: "Changelog", label: "Participants", delta: "+1", name: participant, at: when, by: follower, card: false },
//     { id: `created-${deal?.id}`, type: "Changelog", title: isLeadRecord ? "Lead created" : "Deal created", body: whenLabel, at: when, by: follower, card: false },
//   ];
// };

// function StageChevronBar({ stages, currentStageId, daysInStage, onSelectStage, busy }) {
//   return (
//     <div className="flex w-full overflow-x-auto no-scrollbar">
//       {stages.map((stage, index) => {
//         const active = String(stage.id) === String(currentStageId);
//         const isFirst = index === 0;
//         const isLast = index === stages.length - 1;
//         const label =
//           active && daysInStage != null
//             ? `${daysInStage} day${daysInStage === 1 ? "" : "s"} - ${stage.name}`
//             : stage.name;

//         return (
//           <button
//             key={stage.id}
//             type="button"
//             disabled={busy}
//             onClick={() => onSelectStage?.(stage.id)}
//             title={stage.name}
//             className={`relative flex-1 min-w-[120px] h-9 text-xs font-medium truncate px-3 transition-colors ${
//               active ? "bg-[#26843c] text-white" : "bg-[#e8e8e8] text-gray-700 hover:bg-[#dcdcdc]"
//             } ${busy ? "opacity-70 cursor-wait" : "cursor-pointer"}`}
//             style={{
//               clipPath: isLast
//                 ? isFirst
//                   ? "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
//                   : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)"
//                 : isFirst
//                   ? "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
//                   : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
//               marginLeft: isFirst ? 0 : -8,
//               zIndex: stages.length - index,
//             }}
//           >
//             {label}
//           </button>
//         );
//       })}
//     </div>
//   );
// }

// function SummaryRow({ icon, children }) {
//   return (
//     <div className="flex items-start gap-2.5 py-2 text-sm text-gray-700">
//       <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
//       <div className="min-w-0 flex-1">{children}</div>
//     </div>
//   );
// }

// function SummaryLabelValue({ label, value }) {
//   if (value == null || value === "") return null;
//   return (
//     <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
//       <span className="text-gray-500 shrink-0">{label}</span>
//       <span className="text-right text-gray-900 font-medium break-all">{String(value)}</span>
//     </div>
//   );
// }

// function resolveSourceFields(record) {
//   if (!record) return { origin: "", originId: "", channel: "", channelId: "" };
//   return {
//     origin: record.origin || record.origin_name || record.source_origin || record.source_name || "",
//     originId: record.origin_id ?? record.source_origin_id ?? "",
//     channel: record.channel || record.channel_name || record.source_channel || "",
//     channelId: record.channel_id ?? record.source_channel_id ?? "",
//   };
// }

// function LeadDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const isLeadRecord = location.pathname.includes("/leads-inbox/");

//   const [deal, setDeal] = useState(null);
//   const [stages, setStages] = useState([]);
//   const [pipelineName, setPipelineName] = useState("");
//   const [ownerName, setOwnerName] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);
//   const [error, setError] = useState("Notes");
//   const [notConnected, setNotConnected] = useState(false);
//   const [summaryOpen, setSummaryOpen] = useState(true);
//   const [activityTab, setActivityTab] = useState("Notes");
//   const [historyTab, setHistoryTab] = useState("All");
//   const [fieldFilter, setFieldFilter] = useState("");
//   const DEFAULT_FROM_ACCOUNTS = ["info@hctechinc.com", "support@empivo.com"];

//   const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null });

//   const [emailAccounts, setEmailAccounts] = useState(() => {
//     try {
//       const stored = localStorage.getItem("pipedrive_from_accounts");
//       if (stored) {
//         const parsed = JSON.parse(stored);
//         if (Array.isArray(parsed) && parsed.length) return parsed;
//       }
//     } catch (_) {}
//     return DEFAULT_FROM_ACCOUNTS;
//   });

//   const [emailFrom, setEmailFrom] = useState(() => {
//     try {
//       const last = localStorage.getItem("pipedrive_selected_from_account");
//       if (last) return last;
//     } catch (_) {}
//     return "info@hctechinc.com";
//   });

//   const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
//   const [addingNewAccount, setAddingNewAccount] = useState(false);
//   const [newAccountInput, setNewAccountInput] = useState("");
//   const fromDropdownRef = useRef(null);

//   useEffect(() => {
//     const handleOutsideClick = (e) => {
//       if (fromDropdownRef.current && !fromDropdownRef.current.contains(e.target)) {
//         setFromDropdownOpen(false);
//         setAddingNewAccount(false);
//       }
//     };
//     if (fromDropdownOpen) {
//       document.addEventListener("mousedown", handleOutsideClick);
//     }
//     return () => document.removeEventListener("mousedown", handleOutsideClick);
//   }, [fromDropdownOpen]);

//   const handleSelectFrom = (acc) => {
//     setEmailFrom(acc);
//     setFromDropdownOpen(false);
//     setAddingNewAccount(false);
//     try {
//       localStorage.setItem("pipedrive_selected_from_account", acc);
//     } catch (_) {}
//   };

//   const handleAddNewAccount = (e) => {
//     e?.preventDefault();
//     const clean = newAccountInput.trim();
//     if (!clean) return;
//     if (!isEmail(clean)) {
//       alert("Please enter a valid email address");
//       return;
//     }
//     const updated = Array.from(new Set([...emailAccounts, clean]));
//     setEmailAccounts(updated);
//     setEmailFrom(clean);
//     setNewAccountInput("");
//     setAddingNewAccount(false);
//     setFromDropdownOpen(false);
//     try {
//       localStorage.setItem("pipedrive_from_accounts", JSON.stringify(updated));
//       localStorage.setItem("pipedrive_selected_from_account", clean);
//     } catch (_) {}
//   };

//   const [emailTo, setEmailTo] = useState("");
//   const [emailCc, setEmailCc] = useState("");
//   const [emailBcc, setEmailBcc] = useState("");
//   const [emailSubject, setEmailSubject] = useState("");
//   const [emailBody, setEmailBody] = useState("");
//   const [emailHint, setEmailHint] = useState("");
//   const [noteText, setNoteText] = useState("");
//   const [historyItems, setHistoryItems] = useState([]);
//   const [hideWhatsAppTab, setHideWhatsAppTab] = useState(false);
//   const [mediaBusy, setMediaBusy] = useState(false);
  
//   const [activeCommentId, setActiveCommentId] = useState(null);
//   const [commentText, setCommentText] = useState("");
//   const [commentInputs, setCommentInputs] = useState({});
//   const [collapsedComments, setCollapsedComments] = useState({});
//   const [editingNoteId, setEditingNoteId] = useState(null);
//   const [editingNoteText, setEditingNoteText] = useState("");

//   const fileInputRef = useRef(null);
//   const docInputRef = useRef(null);
//   const seededHistoryForId = useRef(null);

//   useEffect(() => {
//     if (id) markLeadOpened(id);
//   }, [id]);

//   const load = useCallback(async () => {
//     if (!id) return;
//     try {
//       setLoading(true);
//       setError(null);
//       setNotConnected(false);

//       const statusRes = await apiGet(apiPath.pipedriveStatus, {});
//       if (statusRes?.data?.data?.connected !== true) {
//         setNotConnected(true);
//         setDeal(null);
//         return;
//       }

//       if (isLeadRecord) {
//         const leadRes = await apiGet(apiPath.pipedriveLeadById(id), {});
//         let leadData = asOne(leadRes);
//         if (!leadData) {
//           setError("Loading");
//           setDeal(null);
//           return;
//         }

//         if (typeof leadData.person_id === "number" && !leadData.phone && !leadData.person) {
//           try {
//             const pPath = apiPath.pipedrivePersonById
//               ? apiPath.pipedrivePersonById(leadData.person_id)
//               : `/pipedrive/persons/${leadData.person_id}`;
//             const pRes = await apiGet(pPath, {});
//             const pData = asOne(pRes);
//             if (pData) {
//               leadData = {
//                 ...leadData,
//                 person: pData,
//                 person_name: leadData.person_name || pData.name,
//                 phone: pData.phone,
//                 email: pData.email,
//               };
//             }
//           } catch (_) {}
//         }

//         setDeal(leadData);
//         setOwnerName(
//           leadData.owner_name ||
//             (leadData.owner_id != null ? `User ${leadData.owner_id}` : "Owner")
//         );
//         setStages([]);
//         setPipelineName("Leads Inbox");
//         if (leadData.was_seen !== true) {
//           try {
//             await apiPatch(apiPath.pipedriveLeadById(id), { was_seen: true });
//             setDeal((prev) => (prev ? { ...prev, was_seen: true } : prev));
//           } catch {
//             /* ignore */
//           }
//         }
//         return;
//       }

//       const dealRes = await apiGet(apiPath.pipedriveDealById(id), {});
//       let dealData = asOne(dealRes);
//       if (!dealData) {
//         setError("Loading");
//         setDeal(null);
//         return;
//       }

//       if (typeof dealData.person_id === "number" && !dealData.phone && !dealData.person) {
//         try {
//           const pPath = apiPath.pipedrivePersonById
//             ? apiPath.pipedrivePersonById(dealData.person_id)
//             : `/pipedrive/persons/${dealData.person_id}`;
//           const pRes = await apiGet(pPath, {});
//           const pData = asOne(pRes);
//           if (pData) {
//             dealData = {
//               ...dealData,
//               person: pData,
//               person_name: dealData.person_name || pData.name,
//               phone: pData.phone,
//               email: pData.email,
//             };
//           }
//         } catch (_) {}
//       }

//       setDeal(dealData);
//       setOwnerName(resolveOwnerName(dealData) || "Owner");

//       const pipelineId = dealData.pipeline_id;
//       if (pipelineId) {
//         const [stagesRes, pipelinesRes] = await Promise.all([
//           apiGet(apiPath.pipedriveStages, { pipeline_id: pipelineId }),
//           apiGet(apiPath.pipedrivePipelines, {}),
//         ]);
//         let stageRows = asList(stagesRes).filter((s) => String(s.pipeline_id) === String(pipelineId));
//         if (!stageRows.length) stageRows = asList(stagesRes);
//         setStages(
//           stageRows
//             .map((s) => ({
//               id: String(s.id),
//               name: s.name,
//               order: s.order_nr,
//               daysToRotten: s.days_to_rotten,
//               rottenFlag: s.rotten_flag,
//             }))
//             .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
//         );
//         const pipeline = asList(pipelinesRes).find((p) => String(p.id) === String(pipelineId));
//         setPipelineName(pipeline?.name || "Pipeline");
//       } else {
//         setStages([]);
//         setPipelineName("");
//       }
//     } catch (e) {
//       const status = e?.response?.status || e?.status;
//       const body = e?.response?.data || {};
//       if (status === 401 || status === 403 || body?.code === "PIPEDRIVE_NOT_CONNECTED") {
//         setNotConnected(true);
//       } else {
//         setError(body?.message || e?.message || `Failed to load ${isLeadRecord ? "lead" : "deal"}`);
//       }
//       setDeal(null);
//     } finally {
//       setLoading(false);
//     }
//   }, [id, isLeadRecord]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   const title = deal?.title || resolvePersonName(deal) || "Deal";
//   const personName = resolvePersonName(deal) || deal?.person_name || deal?.person?.name || "";
//   const orgName = resolveOrgName(deal) || deal?.org_name || deal?.organization?.name || "";
//   const personEmail = resolvePersonEmail(deal);
//   const personPhone = resolvePersonPhone(deal);
  
//   const dealEmail = isEmail(deal?.cc_email) ? deal.cc_email.trim() : "";
//   const universalEmail = dealEmail ? deriveUniversalEmail(dealEmail) : "";
//   const sourceFields = resolveSourceFields(deal);

//   const whoLabel = () =>
//     deal?.owner_name ||
//     ownerName ||
//     (deal?.owner_id != null ? `User ${deal.owner_id}` : "Devin");

//   const loadDealMedia = useCallback(async (dealId, who) => {
//     if (!dealId) return;
//     const safe = async (promise) => {
//       try {
//         const res = await promise;
//         return Array.isArray(res?.data?.data)
//           ? res.data.data
//           : Array.isArray(res?.data)
//             ? res.data
//             : [];
//       } catch {
//         return [];
//       }
//     };
    
//     const fetchDocs = safe(
//       apiGet(isLeadRecord ? apiPath.leadDocuments(dealId) : apiPath.dealDocuments(dealId), {})
//     );

//     const [notes, files, documents, emails, comments] = await Promise.all([
//       safe(apiGet(isLeadRecord ? apiPath.leadNotes(dealId) : apiPath.dealNotes(dealId), {})),
//       safe(apiGet(isLeadRecord ? apiPath.leadFiles(dealId) : apiPath.dealFiles(dealId), {})),
//       fetchDocs,
//       safe(apiGet(isLeadRecord ? apiPath.leadEmails(dealId) : apiPath.dealEmails(dealId), {})),
//       safe(apiGet(isLeadRecord ? apiPath.leadComments(dealId) : apiPath.dealComments(dealId), {})),
//     ]);
    
//     const docItems = [];
//     const fileItems = [];
//     const seenFileIds = new Set();

//     (documents || []).forEach((d) => {
//       const fid = String(d.id);
//       const tracked = getTrackedFileKind(fid);
//       const desc = String(d.description || "").trim().toLowerCase();
//       if (tracked === "files" || desc === "[empivo-file]") {
//         if (!seenFileIds.has(fid)) {
//           seenFileIds.add(fid);
//           fileItems.push(mapFileToHistory(d, "Files", who));
//         }
//       } else {
//         seenFileIds.add(fid);
//         docItems.push(mapFileToHistory(d, "Documents", who));
//       }
//     });

//     (files || []).forEach((f) => {
//       const fid = String(f.id);
//       if (seenFileIds.has(fid)) return;
//       seenFileIds.add(fid);
//       const tracked = getTrackedFileKind(fid);
//       const desc = String(f.description || "").trim().toLowerCase();
//       if (tracked === "documents" || desc === "[empivo-document]") {
//         docItems.push(mapFileToHistory(f, "Documents", who));
//       } else {
//         fileItems.push(mapFileToHistory(f, "Files", who));
//       }
//     });

//     const commentsByNoteId = {};
//     (comments || []).forEach((c) => {
//       const nid = String(c.note_id || "");
//       if (!commentsByNoteId[nid]) commentsByNoteId[nid] = [];
//       commentsByNoteId[nid].push({
//         id: c.id,
//         noteId: c.note_id,
//         content: stripHtml(c.content || c.comment || ""),
//         at: c.add_time || c.update_time || new Date().toISOString(),
//         by: resolvePartyString(c.user?.name || c.user_id || who, "User"),
//       });
//     });

//     const noteItems = notes.map((n) => {
//       const item = mapNoteToHistory(n, who);
//       item.rawId = n.id;
//       item.pinned = n.pinned_to_deal_flag === 1 || n.pinned_to_lead_flag === 1;
//       item.comments = commentsByNoteId[String(n.id)] || [];
//       return item;
//     });

//     const emailItems = emails.map((m) => mapEmailToHistory(m, who));
//     const when = deal?.add_time || deal?.created_at || new Date().toISOString();
//     const changelog = buildChangelogSeed(deal, who, when, isLeadRecord);
    
//     const hasPipedriveWebFormNote = noteItems.some(
//       (n) => (n.body || "").includes("reCAPTCHA Bot Score") || (n.body || "").includes("Web Form submission summary")
//     );
//     const webform =
//       !hasPipedriveWebFormNote && isLeadRecord
//         ? [buildWebFormSubmissionNote(deal, who, when)]
//         : [];

//     // FIX: Only attach orphan comments if they belong to this entity's notes (never cross-contaminate)
//     const validNoteIds = new Set(notes.map((n) => String(n.id)));
//     const orphanComments = (comments || []).filter(
//       (c) => !c.note_id || !validNoteIds.has(String(c.note_id))
//     );
//     if (orphanComments.length > 0) {
//       const mappedOrphans = orphanComments.map((c) => ({
//         id: c.id,
//         noteId: c.note_id,
//         content: stripHtml(c.content || c.comment || ""),
//         at: c.add_time || c.update_time || new Date().toISOString(),
//         by: resolvePartyString(c.user?.name || c.user_id || who, "User"),
//       }));
//       if (webform.length > 0) {
//         webform[0].comments = [...(webform[0].comments || []), ...mappedOrphans];
//       } else if (noteItems.length > 0) {
//         noteItems[0].comments = [...(noteItems[0].comments || []), ...mappedOrphans];
//       }
//     }
        
//     const merged = [
//       ...noteItems,
//       ...emailItems,
//       ...fileItems,
//       ...docItems,
//       ...webform,
//       ...changelog,
//     ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
//     setHistoryItems(merged);
//   }, [deal, isLeadRecord]);

//   useEffect(() => {
//     if (!deal) return;
//     setEmailTo(resolvePersonEmail(deal) || "");
//     setEmailSubject(`Re: ${deal.title || resolvePersonName(deal) || "Follow up"}`);
//     setEmailBody("");
//     setNoteText("");
//     setEmailHint("");

//     if (seededHistoryForId.current === String(deal.id)) return;
//     seededHistoryForId.current = String(deal.id);
//     setHistoryTab("All");
//     const who = whoLabel();

//     loadDealMedia(deal.id, who);
//   }, [deal?.id, isLeadRecord, loadDealMedia]); // eslint-disable-line react-hooks/exhaustive-deps

//   const addHistory = (item) => {
//     setHistoryItems((prev) => [
//       {
//         id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
//         at: new Date().toISOString(),
//         by: ownerName || "You",
//         card: item.type === "Notes" || item.type === "Emails",
//         ...item,
//       },
//       ...prev,
//     ]);
//   };

//   const openWhatsApp = () => {
//     const phone = personPhone;
//     const url = phone ? `https://wa.me/${phone}` : "https://web.whatsapp.com/";
//     window.open(url, "_blank", "noopener,noreferrer");
//     addHistory({
//       type: "WhatsApp",
//       title: phone ? `WhatsApp opened for ${phone}` : "WhatsApp Web opened",
//       body: personName ? `Contact: ${personName}` : "",
//       card: false,
//     });
//     setHistoryTab("WhatsApp");
//   };

//   const sendEmail = async () => {
//     const to = (emailTo || personEmail || "").trim();
//     if (!isEmail(to)) {
//       alert("Enter a valid To email address");
//       return;
//     }
//     if (!emailSubject.trim() && !emailBody.trim()) {
//       alert("Add a subject or message");
//       return;
//     }

//     try {
//       setMediaBusy(true);
//       setEmailHint("");

//       let payload = null;
//       const sendPath = isLeadRecord ? apiPath.leadEmailSend(deal.id) : apiPath.dealEmailSend(deal.id);
//       const composePath = isLeadRecord ? apiPath.leadEmailCompose(deal.id) : apiPath.dealEmailCompose(deal.id);

//       const emailPayload = {
//         from: (emailFrom || "info@hctechinc.com").trim(),
//         to,
//         cc: emailCc.trim(),
//         bcc: emailBcc.trim(),
//         subject: emailSubject.trim(),
//         body: emailBody.trim(),
//       };

//       try {
//         const res = await apiPost(sendPath, emailPayload);
//         payload = res?.data?.data || res?.data || {};
//       } catch {
//         const res = await apiPost(composePath, emailPayload);
//         payload = res?.data?.data || res?.data || {};
//       }

//       if (payload?.sent) {
//         setEmailHint(payload.smart_bcc ? `Sent · BCC deal ${payload.smart_bcc}` : "Sent");
//       } else if (payload?.mailto) {
//         window.location.href = payload.mailto;
//         setEmailHint("Opened your mail client · send to sync back");
//       }

//       addHistory({
//         type: "Emails",
//         title: emailSubject.trim() || "(No subject)",
//         body: `From: ${emailFrom || "Me"}\nTo: ${to}${emailBody.trim() ? `\n${emailBody.trim().slice(0, 200)}` : ""}${payload?.smart_bcc ? `\n(BCC deal: ${payload.smart_bcc})` : ""}`,
//         by: emailFrom || "Me",
//         card: true,
//       });
//       setHistoryTab("Emails");
//       setEmailBody("");
//       setActivityTab(null);
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Could not send email");
//     } finally {
//       setMediaBusy(false);
//     }
//   };

//   const saveNote = async () => {
//     const text = noteText.trim();
//     if (!text) {
//       alert("Write a note first");
//       return;
//     }

//     try {
//       setMediaBusy(true);
//       const path = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
//       await apiPost(path, { content: text });
//       setNoteText("");
//       setHistoryTab("Notes");
//       setActivityTab(null);
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Could not save note");
//     } finally {
//       setMediaBusy(false);
//     }
//   };

//   const logCall = () => {
//     const phoneToDial = personPhone;
//     if (!phoneToDial) {
//       alert("No phone number available for this contact.");
//       return;
//     }
//     window.location.href = `tel:${phoneToDial}`;
//     addHistory({ type: "Call", title: "Call initiated", body: `Dialed ${phoneToDial}`, card: true });
//     setHistoryTab("All");
//     setActivityTab(null);
//   };

//   const onFilesSelected = async (e, type = "Files") => {
//     const selected = Array.from(e.target.files || []);
//     e.target.value = "";
//     if (!selected.length) return;

//     try {
//       setMediaBusy(true);
//       const kind = type === "Documents" ? "documents" : "files";
//       for (const f of selected) {
//         const fd = new FormData();
//         fd.append("file", f);
//         fd.append("kind", kind);
//         const uploadPath = isLeadRecord ? apiPath.leadMedia(deal.id, kind) : apiPath.dealMedia(deal.id, kind);
//         const res = await apiUpload(uploadPath, fd);
//         const uploadedData = res?.data?.data || res?.data;
//         if (uploadedData?.id) {
//           setTrackedFileKind(uploadedData.id, kind);
//         }
//       }
//       setHistoryTab(type);
//       setActivityTab(null);
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (err) {
//       alert(err?.response?.data?.message || err?.message || `Could not upload ${type.toLowerCase()}`);
//     } finally {
//       setMediaBusy(false);
//     }
//   };

//   const handleSaveComment = async (historyItemId) => {
//     if (!commentText.trim()) return;

//     try {
//       setSaving(true);
//       const rawId = historyItemId ? String(historyItemId).replace(/^(note|comment)-/, "") : "";
//       const cleanNoteId = /^\d+$/.test(rawId) ? rawId : undefined;
//       const path = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
//       await apiPost(path, { content: commentText.trim(), note_id: cleanNoteId });
      
//       setActiveCommentId(null);
//       setCommentText("");
      
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Failed to add comment");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleSaveNoteComment = async (item) => {
//     const text = (commentInputs[item.id] || "").trim();
//     if (!text) return;
//     try {
//       setSaving(true);
//       const cleanNoteId = item.rawId || String(item.id).replace(/^(notes?|webform-note)-/, "");
//       const path = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
//       await apiPost(path, { content: text, note_id: cleanNoteId });

//       setCommentInputs((prev) => ({ ...prev, [item.id]: "" }));
//       setActiveCommentId(null);
//       setCollapsedComments((prev) => ({ ...prev, [item.id]: false }));

//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Failed to add comment");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleDeleteNestedComment = (comment, parentNote) => {
//     setConfirmState({
//       isOpen: true,
//       title: "Confirm Deletion",
//       message: "Are you sure you want to delete this comment?",
//       onConfirm: async () => {
//         setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null });
//         try {
//           setSaving(true);
//           setHistoryItems((prev) =>
//             prev.map((h) => {
//               if (h.id === parentNote.id && h.comments) {
//                 return {
//                   ...h,
//                   comments: h.comments.filter((c) => String(c.id) !== String(comment.id)),
//                 };
//               }
//               return h;
//             })
//           );

//           const basePath = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
//           await apiDelete(`${basePath}?id=${encodeURIComponent(comment.id)}&commentId=${encodeURIComponent(comment.id)}`);
//           await loadDealMedia(deal.id, whoLabel());
//         } catch (e) {
//           alert(e?.response?.data?.message || e?.message || "Failed to delete comment");
//           await loadDealMedia(deal.id, whoLabel());
//         } finally {
//           setSaving(false);
//         }
//       }
//     });
//   };

//   const handleTogglePinNote = async (item) => {
//     try {
//       setSaving(true);
//       const cleanId = item.rawId || String(item.id).replace(/^(notes?|webform-note)-/, "");
//       const newPinned = !item.pinned;
      
//       setHistoryItems((prev) =>
//         prev.map((h) => (h.id === item.id ? { ...h, pinned: newPinned } : h))
//       );

//       const path = isLeadRecord ? apiPath.leadNotes(deal.id, cleanId) : apiPath.dealNotes(deal.id, cleanId);
//       const payload = isLeadRecord
//         ? { pinned_to_lead_flag: newPinned ? 1 : 0 }
//         : { pinned_to_deal_flag: newPinned ? 1 : 0 };
//       await apiPut(path, payload);
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Failed to update pin state");
//       await loadDealMedia(deal.id, whoLabel());
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleStartEditNote = (item) => {
//     setEditingNoteId(item.id);
//     setEditingNoteText(item.body || "");
//   };

//   const handleSaveEditNote = async (item) => {
//     const text = editingNoteText.trim();
//     if (!text) {
//       alert("Note content cannot be empty");
//       return;
//     }
//     try {
//       setSaving(true);
//       const cleanId = String(item.id).replace(/^(notes?|webform-note)-/, "");
//       if (String(item.id).startsWith("webform-note-")) {
//         const createPath = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
//         await apiPost(createPath, { content: text });
//       } else {
//         const editPath = isLeadRecord ? apiPath.leadNotes(deal.id, cleanId) : apiPath.dealNotes(deal.id, cleanId);
//         await apiPut(editPath, { content: text });
//       }
//       setEditingNoteId(null);
//       setEditingNoteText("");
//       await loadDealMedia(deal.id, whoLabel());
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Failed to update note");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleDeleteComment = (item) => {
//     if (!item?.id) return;
//     const isComment = item.type === "Comments" || String(item.id).startsWith("comment-");
//     const isEmail = item.type === "Emails" || String(item.id).startsWith("email-");
//     const isNote = item.type === "Notes" || String(item.id).startsWith("note-");
//     const isDocument = item.type === "Documents" || String(item.id).startsWith("documents-") || String(item.id).startsWith("document-");
//     const isFile = item.type === "Files" || String(item.id).startsWith("files-") || String(item.id).startsWith("file-");
//     const label = isEmail ? "email" : isDocument ? "document" : isFile ? "file" : isComment ? "comment" : isNote ? "note" : "item";

//     setConfirmState({
//       isOpen: true,
//       title: "Confirm Deletion",
//       message: `Are you sure you want to delete this ${label}?`,
//       onConfirm: async () => {
//         setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null });

//         if (String(item.id).startsWith("webform-note-")) {
//           setHistoryItems((prev) => prev.filter((h) => h.id !== item.id));
//           return;
//         }

//         setHistoryItems((prev) => prev.filter((h) => h.id !== item.id));

//         try {
//           setSaving(true);
//           const cleanId = item.fileId || String(item.id).replace(/^(note|notes|comment|comments|email|emails|files?|documents?)-/, "");
//           let basePath;
//           if (isEmail) {
//             basePath = isLeadRecord ? apiPath.leadEmails(deal.id) : apiPath.dealEmails(deal.id);
//           } else if (isDocument) {
//             basePath = isLeadRecord ? apiPath.leadDocuments(deal.id) : apiPath.dealDocuments(deal.id);
//           } else if (isFile) {
//             basePath = isLeadRecord ? apiPath.leadFiles(deal.id) : apiPath.dealFiles(deal.id);
//           } else if (isNote && !isComment) {
//             basePath = isLeadRecord ? apiPath.leadNotes(deal.id) : apiPath.dealNotes(deal.id);
//           } else {
//             basePath = isLeadRecord ? apiPath.leadComments(deal.id) : apiPath.dealComments(deal.id);
//           }

//           const deleteUrl = `${basePath}?id=${encodeURIComponent(cleanId)}&mailId=${encodeURIComponent(cleanId)}&fileId=${encodeURIComponent(cleanId)}&noteId=${encodeURIComponent(cleanId)}&commentId=${encodeURIComponent(cleanId)}`;
//           await apiDelete(deleteUrl);
//           await loadDealMedia(deal.id, whoLabel());
//         } catch (e) {
//           alert(e?.response?.data?.message || e?.message || "Failed to delete item");
//           await loadDealMedia(deal.id, whoLabel());
//         } finally {
//           setSaving(false);
//         }
//       }
//     });
//   };

//   const changeStage = async (stageId) => {
//     if (isLeadRecord || !deal || String(stageId) === String(deal.stage_id) || saving) return;
//     setSaving(true);
//     try {
//       await apiPatch(apiPath.pipedriveDealById(deal.id), { stage_id: Number(stageId) || stageId });
//       setDeal((prev) => prev ? { ...prev, stage_id: stageId, stage_change_time: new Date().toISOString() } : prev);
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || "Failed to update stage");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const markStatus = async (status) => {
//     if (isLeadRecord || !deal || saving) return;
//     setSaving(true);
//     try {
//       await apiPatch(apiPath.pipedriveDealById(deal.id), { status });
//       setDeal((prev) => (prev ? { ...prev, status } : prev));
//     } catch (e) {
//       alert(e?.response?.data?.message || e?.message || `Failed to mark as ${status}`);
//     } finally {
//       setSaving(false);
//     }
//   };

//   const pinnedNotes = useMemo(() => {
//     return historyItems.filter((h) => h.pinned && h.type === "Notes");
//   }, [historyItems]);

//   const filteredHistory = useMemo(() => {
//     let list = historyItems;
//     if (historyTab === "Notes") {
//       list = historyItems.filter((h) => h.type === "Notes" || h.type === "Comments");
//     } else if (historyTab !== "All") {
//       list = historyItems.filter((h) => h.type === historyTab);
//     }
//     return [...list].sort((a, b) => {
//       const aPinned = a.pinned ? 1 : 0;
//       const bPinned = b.pinned ? 1 : 0;
//       if (aPinned !== bPinned) return bPinned - aPinned;
//       return new Date(b.at).getTime() - new Date(a.at).getTime();
//     });
//   }, [historyItems, historyTab]);

//   const historyCount = (type) => historyItems.filter((h) => h.type === type).length;
//   const hasSourceFields = Boolean(
//     sourceFields.origin ||
//       sourceFields.originId ||
//       sourceFields.channel ||
//       sourceFields.channelId
//   );
//   const currentStageId = deal?.stage_id != null ? String(deal.stage_id) : null;
//   const currentStage = stages.find((s) => s.id === currentStageId);
//   const stageChangeTime = deal?.stage_change_time || deal?.update_time || deal?.add_time;
//   const daysInStage = daysSince(stageChangeTime);
//   const rottenThreshold = currentStage?.daysToRotten > 0 ? currentStage.daysToRotten : 7;
//   const isRotting = !isLeadRecord && daysInStage != null && daysInStage >= rottenThreshold && currentStage?.rottenFlag !== false;

//   const formatMoney = (value, currency = "USD") => {
//     const num = Number(value) || 0;
//     try {
//       return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(num);
//     } catch {
//       return `$${num}`;
//     }
//   };

//   const filterQ = fieldFilter.trim().toLowerCase();
//   const showSummary = !filterQ || "summary".includes(filterQ);

//   if (notConnected) {
//     return (
//       <div className="p-6 text-sm text-yellow-800 bg-yellow-50">
//         Pipedrive is not connected.{" "}
//         <a href="/pipedrive-connection" className="font-semibold underline">Go to Integration</a>
//       </div>
//     );
//   }

//   if (loading) {
//     return (
//       <div className="p-6">
//         <p className="text-sm mb-3">{""}</p>
//       </div>
//     );
//   }

//   if (error || !deal) {
//     return (
//       <div className="p-6">
//         <p className="text-sm mb-3">{error || "Loading..."}</p>
//         <button type="button" onClick={() => navigate(-1)} className="text-sm text-blue-600 underline">Go back</button>
//       </div>
//     );
//   }

//   const ownerInitial = (ownerName || "?").charAt(0).toUpperCase();
//   const visibleTabs = hideWhatsAppTab ? ACTIVITY_TABS.filter((t) => t !== "WhatsApp") : ACTIVITY_TABS;

//   return (
//     <div className="flex flex-col h-[calc(100vh-64px)] bg-[#f5f5f5] overflow-hidden">
//       {/* HEADER */}
//       <div className="bg-white border-b border-gray-200 px-4 pt-3 pb-3">
//         <div className="flex items-start justify-between gap-4 flex-wrap">
//           <div className="min-w-0 flex-1">
//             <div className="flex items-center gap-3 flex-wrap">
//               <h1 className="text-xl font-semibold text-gray-900 truncate">{title}</h1>
//               {isRotting && (
//                 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#c62828] text-white text-[11px] font-bold uppercase tracking-wide">
//                   <ClockIcon /> Rotting for {daysInStage} days
//                 </span>
//               )}
//             </div>
//             <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
//               <p>{pipelineName || (isLeadRecord ? "Leads Inbox" : "Pipeline")}</p>
//               {!isLeadRecord && (
//                 <>
//                   <span>→</span>
//                   <span>{currentStage?.name || "Stage"}</span>
//                 </>
//               )}
//             </div>
//           </div>

//           <div className="flex items-center gap-3 flex-shrink-0">
//             <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
//               <div className="w-8 h-8 rounded-full bg-[#317e3e] text-white text-sm font-semibold flex items-center justify-center">
//                 {ownerInitial}
//               </div>
//               <div className="leading-tight">
//                 <div className="text-sm font-medium text-gray-900">{ownerName}</div>
//                 <div className="text-[11px] text-gray-500">Owner</div>
//               </div>
//             </div>
//             {!isLeadRecord && (
//               <>
//                 <button type="button" disabled={saving || deal.status === "won"} onClick={() => markStatus("won")} className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-[#317e3e] hover:bg-[#2a6d35] disabled:opacity-60">Won</button>
//                 <button type="button" disabled={saving || deal.status === "lost"} onClick={() => markStatus("lost")} className="px-4 py-1.5 rounded text-sm font-semibold text-white bg-[#c62828] hover:bg-[#b71c1c] disabled:opacity-60">Lost</button>
//               </>
//             )}
//           </div>
//         </div>

//         {!isLeadRecord && stages.length > 0 && (
//           <div className="mt-3">
//             <StageChevronBar stages={stages} currentStageId={currentStageId} daysInStage={daysInStage} onSelectStage={changeStage} busy={saving} />
//           </div>
//         )}
//       </div>

//       <div className="flex flex-1 min-h-0 overflow-hidden">
//         {/* SIDEBAR */}
//         <aside className="w-[300px] flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
//           {showSummary && (
//             <div className="border-b border-gray-200">
//               <button type="button" onClick={() => setSummaryOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50">
//                 <ChevronDownIcon className={`transition-transform ${summaryOpen ? "" : "-rotate-90"}`} /> Summary
//               </button>
//               {summaryOpen && (
//                 <div className="px-3 pb-3 divide-y divide-gray-100">
//                   <SummaryRow icon={<MoneyIcon />}><span className="font-medium">{formatMoney(deal.value, deal.currency || "USD")}</span></SummaryRow>
//                   <SummaryRow icon={<BuildingIcon />}><span className="font-medium">{orgName || `No Organization`}</span></SummaryRow>
//                   <SummaryRow icon={<UserIcon />}><span><span className="font-medium">{personName || `No Person`}</span></span></SummaryRow>
                  
//                   {personPhone && (
//                     <SummaryRow icon={<PhoneIcon />}>
//                       <a href={`tel:${personPhone}`} className="font-medium text-gray-900 hover:text-blue-600 hover:underline">
//                         {personPhone}
//                       </a>
//                     </SummaryRow>
//                   )}

//                   {dealEmail && (
//                     <SummaryRow icon={<MailIcon />}>
//                       <div className="min-w-0">
//                         <div className="text-xs text-gray-500 mb-0.5">{isLeadRecord ? "Lead specific address" : "Deal specific address"}</div>
//                         <a href={`mailto:${dealEmail}`} className="text-blue-600 hover:underline break-all text-sm" onClick={(e) => e.stopPropagation()}>{dealEmail}</a>
//                       </div>
//                     </SummaryRow>
//                   )}
//                   {universalEmail && universalEmail !== dealEmail && (
//                     <SummaryRow icon={<MailIcon />}>
//                       <div className="min-w-0">
//                         <div className="text-xs text-gray-500 mb-0.5">Universal address</div>
//                         <a href={`mailto:${universalEmail}`} className="text-blue-600 hover:underline break-all text-sm" onClick={(e) => e.stopPropagation()}>{universalEmail}</a>
//                       </div>
//                     </SummaryRow>
//                   )}

//                   {hasSourceFields && (
//                     <div className="py-2">
//                       <SummaryLabelValue label="Source origin" value={sourceFields.origin} />
//                       <SummaryLabelValue label="Source origin ID" value={sourceFields.originId} />
//                       <SummaryLabelValue label="Source channel" value={sourceFields.channel} />
//                       <SummaryLabelValue label="Source channel ID" value={sourceFields.channelId} />
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           )}
//         </aside>

//         {/* MAIN */}
//         <main className="flex-1 overflow-y-auto bg-white">
//           <div className="p-4">
//             <div className="flex items-center justify-between gap-2 border-b border-gray-200 mb-3">
//               <div className="flex items-center gap-0.5 overflow-x-auto no-scrollbar flex-1 min-w-0">
//                 {visibleTabs.map((tab) => {
//                   const active = activityTab === tab;
//                   return (
//                     <button key={tab} type="button" onClick={() => setActivityTab(active ? null : tab)}
//                       className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${active ? "border-blue-600 text-blue-700 font-semibold bg-[#e8f0fe]" : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"}`}>
//                       {tab}
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>

//             {/* Email composer */}
//             {activityTab === "Email" && (
//               <div className="border border-gray-200 rounded-md p-4 space-y-3">
//                 <div className="flex items-center gap-3 border-b border-gray-100 pb-2 mb-2">
//                   <span className="text-sm font-medium text-gray-700 min-w-[40px]">From</span>
                  
//                   <div className="relative" ref={fromDropdownRef}>
//                     <button
//                       type="button"
//                       onClick={() => { setFromDropdownOpen((prev) => !prev); setAddingNewAccount(false); }}
//                       className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-md shadow-sm transition-colors cursor-pointer"
//                     >
//                       <span>{emailFrom || "Select account"}</span>
//                       <ChevronDownIcon className="text-gray-500" />
//                     </button>

//                     {fromDropdownOpen && (
//                       <div className="absolute left-0 top-full mt-1.5 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 text-sm">
//                         <div className="max-h-56 overflow-y-auto">
//                           {emailAccounts.map((acc) => (
//                             <button
//                               key={acc}
//                               type="button"
//                               onClick={() => handleSelectFrom(acc)}
//                               className="w-full flex items-center justify-between px-3.5 py-2 text-left hover:bg-gray-50 transition-colors text-gray-800"
//                             >
//                               <span className="truncate">{acc}</span>
//                               {emailFrom === acc && (
//                                 <span className="text-[#317e3e] font-bold text-base ml-2">✓</span>
//                               )}
//                             </button>
//                           ))}
//                         </div>

//                         <div className="border-t border-gray-100 my-1" />

//                         {!addingNewAccount ? (
//                           <button
//                             type="button"
//                             onClick={() => setAddingNewAccount(true)}
//                             className="w-full flex items-center gap-2 px-3.5 py-2 text-left text-blue-600 hover:bg-blue-50 font-medium transition-colors"
//                           >
//                             <span className="text-base font-bold leading-none">+</span>
//                             <span>New email account</span>
//                           </button>
//                         ) : (
//                           <div className="p-2.5 bg-gray-50 rounded-b-md space-y-2 border-t border-gray-100">
//                             <input
//                               type="email"
//                               autoFocus
//                               value={newAccountInput}
//                               onChange={(e) => setNewAccountInput(e.target.value)}
//                               onKeyDown={(e) => {
//                                 if (e.key === "Enter") handleAddNewAccount(e);
//                                 if (e.key === "Escape") setAddingNewAccount(false);
//                               }}
//                               placeholder="Enter email address..."
//                               className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
//                             />
//                             <div className="flex items-center justify-end gap-1.5">
//                               <button
//                                 type="button"
//                                 onClick={() => { setAddingNewAccount(false); setNewAccountInput(""); }}
//                                 className="px-2.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-100 text-gray-700"
//                               >
//                                 Cancel
//                               </button>
//                               <button
//                                 type="button"
//                                 onClick={handleAddNewAccount}
//                                 className="px-3 py-1 text-xs font-semibold text-white bg-[#317e3e] hover:bg-[#2a6d35] rounded"
//                               >
//                                 Add
//                               </button>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </div>
//                 <div className="flex gap-2">
//                   <div className="flex-1">
//                     <label className="block text-xs text-gray-500 mb-1">To</label>
//                     <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
//                   </div>
//                   <div className="w-1/3">
//                     <label className="block text-xs text-gray-500 mb-1">CC</label>
//                     <input type="email" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} placeholder="cc@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
//                   </div>
//                   <div className="w-1/3">
//                     <label className="block text-xs text-gray-500 mb-1">BCC</label>
//                     <input type="email" value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} placeholder="bcc@example.com" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
//                   </div>
//                 </div>
//                 <div>
//                   <label className="block text-xs text-gray-500 mb-1">Subject</label>
//                   <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-gray-500 mb-1">Message</label>
//                   <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={8} placeholder="Write your email..." className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y" />
//                 </div>
//                 {emailHint && <p className="text-xs text-[#317e3e]">{emailHint}</p>}
//                 <div className="flex justify-end gap-2">
//                   <button type="button" onClick={() => setActivityTab(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
//                   <button type="button" disabled={mediaBusy} onClick={sendEmail} className="px-4 py-1.5 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60">{mediaBusy ? "Sending…" : "Send"}</button>
//                 </div>
//               </div>
//             )}

//             {/* Call */}
//             {activityTab === "Call" && (
//               <div className="border border-gray-200 rounded-md p-6 flex flex-col items-center justify-center text-center space-y-4">
//                 <div className="w-16 h-16 bg-[#e8f5e9] text-[#2e7d32] rounded-full flex items-center justify-center">
//                   <PhoneIcon />
//                 </div>
//                 <div>
//                   <h3 className="text-lg font-semibold text-gray-800">{personName || "Contact"}</h3>
//                   <p className="text-gray-500 text-sm font-medium">{personPhone || "No phone number available"}</p>
//                 </div>
//                 <button
//                   type="button"
//                   onClick={logCall}
//                   disabled={!personPhone}
//                   className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
//                 >
//                   <PhoneIcon /> Start Call
//                 </button>
//               </div>
//             )}

//             {/* WhatsApp */}
//             {activityTab === "WhatsApp" && (
//               <div className="border border-gray-200 rounded-md p-5">
//                 <h3 className="text-base font-semibold text-gray-800 mb-4">Discover WhatsApp</h3>
//                 <ul className="space-y-3 mb-5">
//                   {["Link WhatsApp conversations to persons and deals", "Chat with customers through Pipedrive and use WhatsApp templates"].map((line) => (
//                     <li key={line} className="flex items-start gap-2.5 text-sm text-gray-700">
//                       <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f5e9] text-[#2e7d32] flex items-center justify-center text-xs font-bold">✓</span>
//                       <span>{line}</span>
//                     </li>
//                   ))}
//                 </ul>
//                 <div className="flex flex-wrap items-center gap-3">
//                   <button type="button" onClick={openWhatsApp} className="px-4 py-2 text-sm font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35]">Connect</button>
//                 </div>
//               </div>
//             )}

//             {/* Files */}
//             {activityTab === "Files" && (
//               <div className="border border-dashed border-gray-300 rounded-md p-8 flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
//                 <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => onFilesSelected(e, "Files")} />
//                 <div className="flex flex-wrap items-center justify-center gap-3">
//                   <button type="button" disabled={mediaBusy} className="px-4 py-2 text-sm font-semibold text-white bg-[#317e3e] rounded disabled:opacity-60" onClick={() => fileInputRef.current?.click()}>{mediaBusy ? "Uploading…" : "Upload files"}</button>
//                   <span>or drag files here.</span>
//                 </div>
//               </div>
//             )}

//             {/* Notes */}
//             {activityTab === "Notes" && (
//               <div className="border border-gray-200 rounded-md bg-[#fffde7] p-3">
//                 <textarea rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a note..." className="w-full bg-transparent text-sm focus:outline-none resize-y" />
//                 <div className="flex justify-end gap-2 mt-2">
//                   <button type="button" className="px-3 py-1.5 text-sm text-gray-600" onClick={() => { setNoteText(""); setActivityTab(null); }}>Cancel</button>
//                   <button type="button" disabled={mediaBusy} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#317e3e] rounded disabled:opacity-60" onClick={saveNote}>{mediaBusy ? "Saving…" : "Save"}</button>
//                 </div>
//               </div>
//             )}

//             {/* Documents */}
//             {activityTab === "Documents" && (
//               <div className="border border-gray-200 rounded-md overflow-hidden">
//                 <div className="flex items-center gap-4 px-4 py-2.5 border-b border-gray-100">
//                   <span className="text-sm font-semibold text-blue-700 border-b-2 border-blue-600 pb-2 -mb-2.5">Documents</span>
//                 </div>
//                 <div className="px-4 py-3 flex flex-wrap items-center gap-4 border-b border-gray-100">
//                   <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv" className="hidden" onChange={(e) => onFilesSelected(e, "Documents")} />
//                   <button type="button" disabled={mediaBusy} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60" onClick={() => docInputRef.current?.click()}>
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
//                     {mediaBusy ? "Uploading…" : "Upload from device"}
//                   </button>
//                 </div>
//               </div>
//             )}

//             {/* Focus */}
//             <div className="mt-6">
//               <div className="flex items-center justify-between mb-2">
//                 <h2 className="text-sm font-semibold text-gray-800">Focus</h2>
//                 {pinnedNotes.length > 0 && (
//                   <span className="text-xs text-amber-800 bg-amber-100 font-semibold px-2 py-0.5 rounded">
//                     {pinnedNotes.length} pinned {pinnedNotes.length === 1 ? "note" : "notes"}
//                   </span>
//                 )}
//               </div>
//               {pinnedNotes.length === 0 ? (
//                 <p className="text-sm text-gray-400">No focus items yet.</p>
//               ) : (
//                 <div className="space-y-2.5">
//                   {pinnedNotes.map((pn) => (
//                     <div key={`focus-${pn.id}`} className="bg-[#fff8d1] border-2 border-amber-400 rounded-lg p-3 text-sm shadow-2xs">
//                       <div className="flex items-center justify-between gap-2 mb-1">
//                         <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-200/90 px-2 py-0.5 rounded">
//                           <PinIcon pinned={true} className="w-3 h-3 text-amber-900" />
//                           <span>Pinned Note</span>
//                         </span>
//                         <div className="flex items-center gap-1.5">
//                           <span className="text-xs text-gray-500 font-medium">{formatHistoryWhen(pn.at)}</span>
//                           <button
//                             type="button"
//                             onClick={() => handleTogglePinNote(pn)}
//                             className="p-1 text-amber-700 hover:text-amber-900 rounded"
//                             title="Unpin note"
//                           >
//                             <PinIcon pinned={true} className="w-3.5 h-3.5" />
//                           </button>
//                         </div>
//                       </div>
//                       <div className="text-gray-800 text-sm whitespace-pre-wrap break-words">
//                         {pn.body}
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* History */}
//             <div>
//               <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">History</h2>
//               <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-gray-100 mb-4">
//                 {HISTORY_TABS.map((tab) => {
//                   const count = ["Notes", "Emails", "WhatsApp", "Files", "Documents"].includes(tab) ? historyCount(tab) : null;
//                   return (
//                     <button key={tab} type="button" onClick={() => setHistoryTab(tab)}
//                       className={`px-2.5 py-1.5 text-xs whitespace-nowrap rounded-md ${historyTab === tab ? "bg-[#e8f0fe] text-blue-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}>
//                       {tab}{count != null && count > 0 ? ` (${count})` : ""}
//                     </button>
//                   );
//                 })}
//               </div>

//               {filteredHistory.length === 0 ? (
//                 <div className="py-10 text-center text-gray-400">
//                   {historyTab === "Emails" ? (
//                     <div className="text-sm font-semibold text-gray-700">No emails created yet</div>
//                   ) : historyTab === "Notes" ? (
//                     <div className="text-sm font-semibold text-gray-700">No notes yet</div>
//                   ) : historyTab === "WhatsApp" ? (
//                     <div className="text-sm font-semibold text-gray-700">No WhatsApp activity yet</div>
//                   ) : historyTab === "Files" ? (
//                     <div className="text-sm font-semibold text-gray-700">No files uploaded yet</div>
//                   ) : historyTab === "Documents" ? (
//                     <div className="px-4 py-8 text-center text-gray-500">
//                       <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Documents created</div>
//                       <div className="text-sm font-semibold text-gray-700">{historyCount("Documents") === 0 ? "No documents created yet" : `${historyCount("Documents")} document(s) — see History below`}</div>
//                     </div>
//                   ) : (
//                     <div className="text-sm">No history yet.</div>
//                   )}
//                 </div>
//               ) : (
//                 <div className="relative pl-6 space-y-4">
//                   <div className="absolute left-[7px] top-2 bottom-2 border-l border-dashed border-gray-300" />
//                   {filteredHistory.map((item) => (
//                     <div key={item.id} className="relative">
//                       <div className={`absolute -left-6 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow flex items-center justify-center ${item.card ? "bg-white text-gray-500" : "bg-white border-gray-300"}`}>
//                         {item.type === "Notes" ? (<span className="text-[8px]">📝</span>) : item.type === "Emails" ? (<span className="text-[8px]">✉</span>) : item.type === "WhatsApp" ? (<span className="text-[8px]">💬</span>) : item.type === "Files" || item.type === "Documents" ? (<span className="text-[8px]">📎</span>) : (<span className="w-1.5 h-1.5 rounded-full bg-gray-300 block" />)}
//                       </div>

//                       {item.card ? (
//                         <div className={`rounded-lg p-3.5 text-sm shadow-2xs transition-all ${
//                           item.pinned 
//                             ? "bg-[#fff8d1] border-2 border-amber-400 ring-1 ring-amber-300/40" 
//                             : "bg-[#fff9db] border border-[#fde68a]"
//                         }`}>
//                           <div className="flex items-start justify-between gap-2 mb-1.5">
//                             <div className="flex items-center gap-2 flex-wrap">
//                               <span className="text-xs text-gray-500 font-medium">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</span>
//                               {item.pinned && (
//                                 <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-200/90 px-2 py-0.5 rounded shadow-2xs">
//                                   <PinIcon pinned={true} className="w-3 h-3 text-amber-900" />
//                                   <span>Pinned</span>
//                                 </span>
//                               )}
//                             </div>
//                             <div className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
//                               {!isLeadRecord && item.type === "Notes" && item.comments && item.comments.length > 0 && (
//                                 <button
//                                   type="button"
//                                   onClick={() => setCollapsedComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
//                                   className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded transition-colors cursor-pointer mr-0.5 ${
//                                     collapsedComments[item.id]
//                                       ? "bg-amber-100 hover:bg-amber-200 text-gray-700"
//                                       : "bg-amber-200 hover:bg-amber-300/80 text-gray-900 shadow-2xs"
//                                   }`}
//                                   title={collapsedComments[item.id] ? "Show comments" : "Hide comments"}
//                                 >
//                                   <span>{item.comments.length} {item.comments.length === 1 ? "comment" : "comments"}</span>
//                                   <span className="text-[9px]">{collapsedComments[item.id] ? "▼" : "▲"}</span>
//                                 </button>
//                               )}

//                               {!isLeadRecord && item.type === "Notes" && (!item.comments || item.comments.length === 0) && (
//                                 <button
//                                   type="button"
//                                   onClick={() => setCollapsedComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
//                                   className={`p-1 transition-colors rounded ${
//                                     collapsedComments[item.id]
//                                       ? "text-gray-600 bg-gray-50"
//                                       : "text-gray-400 hover:text-gray-600 hover:bg-amber-100/50"
//                                   }`}
//                                   title={collapsedComments[item.id] ? "Close comment box" : "Add comment"}
//                                 >add comment
//                                 </button>
//                               )}

//                               {item.type === "Notes" && (
//                                 <button
//                                   type="button"
//                                   onClick={() => handleStartEditNote(item)}
//                                   className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded hover:bg-amber-100/40"
//                                   title="Edit note"
//                                 >
//                                   <MdEdit size={16} />
//                                 </button>
//                               )}

//                               {(item.type === "Notes" || item.type === "Emails") && (
//                                 <button
//                                   type="button"
//                                   disabled={saving}
//                                   onClick={() => handleDeleteComment(item)}
//                                   className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded disabled:opacity-50 hover:bg-amber-100/40"
//                                   title={`Delete ${item.type === "Emails" ? "email" : "note"}`}
//                                 >
//                                   <MdDelete size={16} />
//                                 </button>
//                               )}
//                             </div>
//                           </div>

//                           {item.type === "Emails" && item.title && item.title !== "Email" && (
//                             <div className="font-semibold text-gray-900 underline decoration-gray-400 mb-1">{item.title}</div>
//                           )}

//                           {editingNoteId === item.id ? (
//                             <div className="mt-2 space-y-2">
//                               <textarea
//                                 rows={4}
//                                 value={editingNoteText}
//                                 onChange={(e) => setEditingNoteText(e.target.value)}
//                                 className="w-full text-sm p-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
//                                 autoFocus
//                               />
//                               <div className="flex justify-end gap-2">
//                                 <button
//                                   type="button"
//                                   onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
//                                   className="px-3 py-1 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-700"
//                                 >
//                                   Cancel
//                                 </button>
//                                 <button
//                                   type="button"
//                                   disabled={saving || !editingNoteText.trim()}
//                                   onClick={() => handleSaveEditNote(item)}
//                                   className="px-3 py-1 text-xs font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60"
//                                 >
//                                   {saving ? "Saving…" : "Save"}
//                                 </button>
//                               </div>
//                             </div>
//                           ) : (
//                             <div className="text-gray-800 text-sm my-1.5 whitespace-pre-wrap break-words leading-relaxed">
//                               {item.body}
//                             </div>
//                           )}

//                           {!isLeadRecord && item.type === "Notes" && (item.comments?.length > 0 ? !collapsedComments[item.id] : Boolean(collapsedComments[item.id])) && (
//                             <div className="mt-3 pt-1">
//                               <div className="flex items-center gap-2.5">
//                                 <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 font-semibold flex items-center justify-center text-xs flex-shrink-0">
//                                   {(ownerName || "D").charAt(0).toUpperCase()}
//                                 </div>
//                                 <div className="flex-1 relative">
//                                   <input
//                                     type="text"
//                                     value={commentInputs[item.id] || ""}
//                                     onChange={(e) => setCommentInputs({ ...commentInputs, [item.id]: e.target.value })}
//                                     onFocus={() => setActiveCommentId(item.id)}
//                                     onKeyDown={(e) => {
//                                       if (e.key === "Enter" && !e.shiftKey) {
//                                         e.preventDefault();
//                                         handleSaveNoteComment(item);
//                                       }
//                                     }}
//                                     placeholder="Write a comment, @name..."
//                                     className="w-full bg-white border border-gray-200 rounded-md px-3.5 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-2xs"
//                                   />
//                                 </div>
//                               </div>
//                               {activeCommentId === item.id && (commentInputs[item.id] || "").trim() && (
//                                 <div className="flex justify-end gap-2 mt-2 pl-9">
//                                   <button
//                                     type="button"
//                                     onClick={() => {
//                                       setActiveCommentId(null);
//                                       setCommentInputs({ ...commentInputs, [item.id]: "" });
//                                     }}
//                                     className="px-2.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 text-gray-600"
//                                   >
//                                     Cancel
//                                   </button>
//                                   <button
//                                     type="button"
//                                     disabled={saving || !(commentInputs[item.id] || "").trim()}
//                                     onClick={() => handleSaveNoteComment(item)}
//                                     className="px-3.5 py-1 text-xs font-semibold text-white bg-[#317e3e] rounded hover:bg-[#2a6d35] disabled:opacity-60"
//                                   >
//                                     {saving ? "Saving…" : "Save"}
//                                   </button>
//                                 </div>
//                               )}
//                             </div>
//                           )}

//                           {!isLeadRecord && item.type === "Notes" && item.comments && item.comments.length > 0 && !collapsedComments[item.id] && (
//                             <div className="mt-3 pt-1 space-y-3">
//                               {item.comments.map((c) => (
//                                 <div key={c.id} className="flex items-start gap-2.5 group">
//                                   <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-800 font-semibold flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
//                                     {(c.by || "D").charAt(0).toUpperCase()}
//                                   </div>
//                                   <div className="flex-1 min-w-0">
//                                     <div className="flex items-center justify-between gap-2">
//                                       <div className="text-xs text-gray-600">
//                                         <span className="font-semibold text-gray-900">{resolvePartyString(c.by, "User")}</span>
//                                         <span className="mx-1 text-gray-400">·</span>
//                                         <span className="text-gray-500">{formatHistoryWhen(c.at)}</span>
//                                       </div>
//                                       <button
//                                         type="button"
//                                         disabled={saving}
//                                         onClick={() => handleDeleteNestedComment(c, item)}
//                                         className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded opacity-60 hover:opacity-100"
//                                         title="Delete comment"
//                                       >
//                                         <MdDelete size={14} />
//                                       </button>
//                                     </div>
//                                     <div className="text-sm text-gray-800 whitespace-pre-wrap break-words mt-0.5 leading-relaxed">
//                                       {c.content}
//                                     </div>
//                                   </div>
//                                 </div>
//                               ))}
//                             </div>
//                           )}
//                         </div>
//                       ) : item.type === "Changelog" && item.delta ? (
//                         <div className="text-sm pt-0.5">
//                           <div className="text-gray-900">{item.label} : <span className="text-[#317e3e] font-medium">{item.delta}</span> {resolvePartyString(item.name)}</div>
//                           <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
//                         </div>
//                       ) : item.type === "Changelog" ? (
//                         <div className="text-sm pt-0.5">
//                           <div className="text-gray-900">{item.title} : {item.body}</div>
//                           <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
//                         </div>
//                       ) : item.type === "Files" || item.type === "Documents" ? (
//                         <div className="flex items-center justify-between text-sm py-1 bg-white border border-gray-200 rounded px-3 py-2 shadow-sm">
//                           <div className="flex items-center gap-2 min-w-0">
//                             <span className="text-gray-400 shrink-0">📎</span>
//                             <div className="min-w-0">
//                               {item.url ? (
//                                 <a
//                                   href={item.url}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="font-medium text-blue-600 hover:underline truncate block"
//                                   title="Download / view file"
//                                 >
//                                   {item.title}
//                                 </a>
//                               ) : (
//                                 <span className="font-medium text-gray-900 truncate block">{item.title}</span>
//                               )}
//                               <div className="text-xs text-gray-500">
//                                 {item.body ? <span>{item.body} · </span> : null}
//                                 {formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}
//                               </div>
//                             </div>
//                           </div>
//                           <button
//                             type="button"
//                             disabled={saving}
//                             onClick={() => handleDeleteComment(item)}
//                             className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded disabled:opacity-50 shrink-0 ml-2"
//                             title={`Delete ${item.type === "Documents" ? "document" : "file"}`}
//                           >
//                             <MdDelete size={16} />
//                           </button>
//                         </div>
//                       ) : (
//                         <div className="text-sm">
//                           <div className="text-gray-900">
//                             <span className="font-medium">{item.title}</span>
//                             {item.body ? (<span className="text-gray-600">{item.title?.endsWith(":") ? " " : " : "}{item.body}</span>) : null}
//                           </div>
//                           <div className="text-xs text-gray-500 mt-0.5">{formatHistoryWhen(item.at)} · {resolvePartyString(item.by)}</div>
//                         </div>
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>
//           </div>
//         </main>
//       </div>

//       {confirmState.isOpen && (
//         <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
//             <div className="p-4">
//               <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmState.title}</h3>
//               <p className="text-sm text-gray-600 mb-6">{confirmState.message}</p>
//               <div className="flex justify-end gap-3">
//                 <button
//                   type="button"
//                   onClick={() => setConfirmState({ isOpen: false, title: "", message: "", onConfirm: null })}
//                   className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="button"
//                   onClick={confirmState.onConfirm}
//                   className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
//                 >
//                   OK
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       <style>{`
//         .no-scrollbar::-webkit-scrollbar { display: none; }
//         .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
//       `}</style>
//     </div>
//   );
// }

// export default LeadDetail;